import { useState, useEffect, useCallback, useRef } from 'react';

// ═══════════════════════════════════════════════════════════════
// RECORDING ANIMATION COMPONENT
// ═══════════════════════════════════════════════════════════════
const RecordingAnimation = ({ onComplete, sliderPrep, sliderPerf, sliderProd }) => {
  const [progress, setProgress] = useState(0);
  const [bubbles, setBubbles] = useState([]);
  const [scores, setScores] = useState({ prep: 0, perf: 0, prod: 0 });
  const bubbleIdRef = useRef(0);
  const hasCompletedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  
  // Keep onComplete ref updated
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(p => {
        if (p >= 100) {
          clearInterval(interval);
          // Only call onComplete once
          if (!hasCompletedRef.current) {
            hasCompletedRef.current = true;
            setTimeout(() => onCompleteRef.current(), 800);
          }
          return 100;
        }
        return p + 0.8;
      });

      // Spawn bubbles randomly based on slider weights
      if (Math.random() > 0.6) {
        const total = sliderPrep + sliderPerf + sliderProd;
        const rnd = Math.random() * total;
        let type = 'prep';
        if (rnd < sliderPrep) type = 'prep';
        else if (rnd < sliderPrep + sliderPerf) type = 'perf';
        else type = 'prod';

        const value = Math.ceil(Math.random() * 3);
        const newBubble = {
          id: bubbleIdRef.current++,
          type,
          value,
          x: 20 + Math.random() * 60,
          y: 30 + Math.random() * 30
        };

        setBubbles(b => [...b, newBubble]);
        setScores(s => ({ ...s, [type]: s[type] + value }));

        // Remove bubble after animation
        setTimeout(() => {
          setBubbles(b => b.filter(bubble => bubble.id !== newBubble.id));
        }, 1400);
      }
    }, 25);

    return () => clearInterval(interval);
  }, [sliderPrep, sliderPerf, sliderProd]);

  const getBubbleStyle = (type) => {
    switch(type) {
      case 'prep': return { background: 'radial-gradient(circle at 30% 30%, #00ccff, #0099cc)', borderColor: '#00ccff' };
      case 'perf': return { background: 'radial-gradient(circle at 30% 30%, #ff6b6b, #cc4444)', borderColor: '#ff6b6b' };
      case 'prod': return { background: 'radial-gradient(circle at 30% 30%, #ffcc00, #cc9900)', borderColor: '#ffcc00' };
      default: return {};
    }
  };

  return (
    <div style={{ 
      width: '100%', 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative'
    }}>
      {/* Live score pills */}
      <div style={{ 
        position: 'absolute', 
        top: '80px', 
        display: 'flex', 
        gap: '20px',
        zIndex: 10
      }}>
        <div style={{
          background: '#1a1a2e',
          border: '2px solid #00ccff',
          borderRadius: '50px',
          padding: '10px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#00ccff',
          fontWeight: 'bold',
          minWidth: '100px',
          justifyContent: 'center'
        }}>
          📝 <span>{scores.prep}</span>
        </div>
        <div style={{
          background: '#1a1a2e',
          border: '2px solid #ff6b6b',
          borderRadius: '50px',
          padding: '10px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#ff6b6b',
          fontWeight: 'bold',
          minWidth: '100px',
          justifyContent: 'center'
        }}>
          🎭 <span>{scores.perf}</span>
        </div>
        <div style={{
          background: '#1a1a2e',
          border: '2px solid #ffcc00',
          borderRadius: '50px',
          padding: '10px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#ffcc00',
          fontWeight: 'bold',
          minWidth: '100px',
          justifyContent: 'center'
        }}>
          🎚️ <span>{scores.prod}</span>
        </div>
      </div>

      {/* Bubbles layer */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
        {bubbles.map(bubble => (
          <div
            key={bubble.id}
            style={{
              position: 'absolute',
              left: `${bubble.x}%`,
              top: `${bubble.y}%`,
              width: '45px',
              height: '45px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '14px',
              border: '2px solid rgba(255,255,255,0.6)',
              boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
              animation: 'floatUp 1.4s ease-out forwards',
              ...getBubbleStyle(bubble.type)
            }}
          >
            +{bubble.value}
          </div>
        ))}
      </div>

      {/* Microphone */}
      <div style={{
        width: '120px',
        height: '120px',
        background: '#1a1a2e',
        borderRadius: '50%',
        border: '4px solid #ff3333',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 0 30px rgba(255, 51, 51, 0.5)',
        animation: 'pulse 2s infinite',
        marginBottom: '30px'
      }}>
        <span style={{ fontSize: '50px' }}>🎙️</span>
      </div>

      {/* ON AIR text */}
      <div style={{
        fontSize: '32px',
        fontWeight: 'bold',
        color: '#ff3333',
        letterSpacing: '8px',
        marginBottom: '30px',
        textShadow: '0 0 20px rgba(255, 51, 51, 0.5)'
      }}>
        ON AIR
      </div>

      {/* Progress bar */}
      <div style={{
        width: '350px',
        height: '20px',
        background: '#252540',
        borderRadius: '10px',
        overflow: 'hidden',
        border: '2px solid #333'
      }}>
        <div style={{
          width: `${progress}%`,
          height: '100%',
          background: 'linear-gradient(90deg, #ff3333, #ff6666)',
          transition: 'width 0.1s',
          borderRadius: '8px'
        }} />
      </div>

      {/* CSS Keyframes */}
      <style>{`
        @keyframes floatUp {
          0% { transform: translateY(0) scale(0.5); opacity: 0; }
          20% { transform: translateY(-20px) scale(1.1); opacity: 1; }
          100% { transform: translateY(-120px) scale(1); opacity: 0; }
        }
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 30px rgba(255, 51, 51, 0.5); }
          50% { box-shadow: 0 0 50px rgba(255, 51, 51, 0.8); }
        }
        @keyframes popIn {
          0% { transform: scale(0.8); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

// Results Animation Component - Game Dev Tycoon style
const ResultsAnimation = ({ 
  categories, 
  finalScore, 
  episodeResults, 
  formatNumber,
  boostsUsed = [],
  trendBonus = false,
  onContinue 
}) => {
  const [visibleCategories, setVisibleCategories] = useState(0);
  const [categoryScores, setCategoryScores] = useState([null, null, null]); // 3 categories now
  const [showFinalScore, setShowFinalScore] = useState(false);
  const [rollingFinalScore, setRollingFinalScore] = useState(null);
  const [finalScoreLocked, setFinalScoreLocked] = useState(false);
  const [showGains, setShowGains] = useState(false);
  const [showButton, setShowButton] = useState(false);
  
  // Store categories in ref to avoid re-render issues
  const categoriesRef = useRef(categories);
  useEffect(() => {
    categoriesRef.current = categories;
  }, [categories]);

  // Generate stars for a score
  const getStars = (score) => {
    const fullStars = Math.floor(score / 2);
    const hasHalfStar = (score % 2) >= 1;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    
    return (
      <span style={{ whiteSpace: 'nowrap' }}>
        {'★'.repeat(fullStars)}
        {hasHalfStar && (
          <span style={{ 
            display: 'inline-block', 
            position: 'relative',
            width: '1em'
          }}>
            <span style={{ color: '#444' }}>★</span>
            <span style={{ 
              position: 'absolute', 
              left: 0, 
              top: 0,
              width: '0.5em', 
              overflow: 'hidden',
              color: 'inherit'
            }}>★</span>
          </span>
        )}
        <span style={{ color: '#444' }}>{'★'.repeat(emptyStars)}</span>
      </span>
    );
  };

  const getScoreColor = (score) => {
    if (score >= 8) return '#00ff88';
    if (score >= 6) return '#ffcc00';
    if (score >= 4) return '#ff9900';
    return '#ff6b6b';
  };

  // Animate categories appearing one by one
  useEffect(() => {
    if (visibleCategories >= categoriesRef.current.length) {
      // All categories shown, now show final score
      if (!showFinalScore) {
        const timeout = setTimeout(() => setShowFinalScore(true), 500);
        return () => clearTimeout(timeout);
      }
      return;
    }

    // Start rolling animation for this category
    const catIndex = visibleCategories;
    const targetScore = categoriesRef.current[catIndex].score;
    
    const startDelay = setTimeout(() => {
      // Roll the score
      let rollCount = 0;
      const rollInterval = setInterval(() => {
        // Random score near the target
        const variance = Math.max(0.5, (15 - rollCount) * 0.4);
        const randomScore = Math.max(1, Math.min(10, targetScore + (Math.random() - 0.5) * variance * 2));
        setCategoryScores(prev => {
          const newScores = [...prev];
          newScores[catIndex] = randomScore;
          return newScores;
        });
        rollCount++;
        
        if (rollCount >= 15) {
          clearInterval(rollInterval);
          // Lock in final score
          setCategoryScores(prev => {
            const newScores = [...prev];
            newScores[catIndex] = targetScore;
            return newScores;
          });
          // Move to next category after a small delay
          setTimeout(() => {
            setVisibleCategories(v => v + 1);
          }, 200);
        }
      }, 60);

      return () => clearInterval(rollInterval);
    }, catIndex === 0 ? 400 : 500);

    return () => clearTimeout(startDelay);
  }, [visibleCategories, showFinalScore]);

  // Animate final score rolling - SLOWER and more dramatic
  useEffect(() => {
    if (showFinalScore && !finalScoreLocked) {
      let rollCount = 0;
      const totalRolls = 50; // More rolls for longer animation
      
      const rollInterval = setInterval(() => {
        // Random score that gets closer to final over time
        const progress = rollCount / totalRolls;
        // Start with big variance, end with tiny variance
        const variance = Math.max(0.1, (1 - progress * progress) * 4);
        const randomScore = Math.max(1, Math.min(10, finalScore + (Math.random() - 0.5) * variance * 2));
        setRollingFinalScore(randomScore);
        rollCount++;

        if (rollCount >= totalRolls) {
          clearInterval(rollInterval);
          setRollingFinalScore(finalScore);
          setFinalScoreLocked(true);
          // Show gains after a short delay
          setTimeout(() => setShowGains(true), 600);
          setTimeout(() => setShowButton(true), 900);
        }
      }, 50); // 50ms × 50 rolls = 2.5 seconds total

      return () => clearInterval(rollInterval);
    }
  }, [showFinalScore, finalScoreLocked, finalScore]);

  const displayFinalScore = finalScoreLocked ? finalScore : (rollingFinalScore || finalScore);

  return (
    <div style={{
      background: '#1a1a2e',
      border: '3px solid #ffcc00',
      borderRadius: '12px',
      padding: '30px',
      maxWidth: '450px',
      width: '90%',
      maxHeight: '90vh',
      overflowY: 'auto',
      textAlign: 'center',
      animation: 'popIn 0.3s ease-out'
    }}>
      <h2 style={{ margin: '0 0 20px 0', color: '#ffcc00' }}>📊 Résultats</h2>

      {/* Category scores */}
      <div style={{ marginBottom: '20px', minHeight: '160px' }}>
        {categories.map((cat, i) => (
          <div 
            key={i} 
            style={{
              background: '#252540',
              borderRadius: '8px',
              padding: '12px 15px',
              marginBottom: '8px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              opacity: i < visibleCategories || (i === visibleCategories && categoryScores[i] !== null) ? 1 : 0,
              transform: i < visibleCategories || (i === visibleCategories && categoryScores[i] !== null) ? 'translateX(0)' : 'translateX(-20px)',
              transition: 'opacity 0.3s, transform 0.3s'
            }}
          >
            <div style={{ textAlign: 'left' }}>
              <div style={{ color: '#fff', fontWeight: 'bold', fontSize: '14px' }}>
                {cat.name}
              </div>
              <div style={{ color: '#666', fontSize: '11px' }}>
                {cat.subtitle}
                {cat.penalty && <span style={{ color: '#ff6b6b' }}> {cat.penalty}</span>}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              {categoryScores[i] !== null && (
                <>
                  <div style={{ 
                    color: getScoreColor(categoryScores[i]), 
                    fontSize: '14px', 
                    letterSpacing: '2px',
                    transition: 'color 0.1s'
                  }}>
                    {getStars(categoryScores[i])}
                  </div>
                  <div style={{ 
                    color: getScoreColor(categoryScores[i]), 
                    fontSize: '12px', 
                    fontWeight: 'bold',
                    transition: 'color 0.1s'
                  }}>
                    {categoryScores[i].toFixed(1)}/10
                  </div>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Slider Feedback (critique) */}
      {episodeResults.sliderFeedback && visibleCategories >= 2 && (
        <div style={{
          background: episodeResults.sliderFeedback.type === 'success' ? '#00ff8811' : '#ffcc0011',
          borderRadius: '8px',
          padding: '12px 15px',
          marginBottom: '15px',
          border: `1px solid ${episodeResults.sliderFeedback.type === 'success' ? '#00ff8844' : '#ffcc0044'}`,
          opacity: visibleCategories >= 2 ? 1 : 0,
          transform: visibleCategories >= 2 ? 'translateY(0)' : 'translateY(-10px)',
          transition: 'opacity 0.4s, transform 0.4s'
        }}>
          <div style={{ 
            color: episodeResults.sliderFeedback.type === 'success' ? '#00ff88' : '#ffcc00', 
            fontSize: '13px',
            fontStyle: 'italic'
          }}>
            💬 "{episodeResults.sliderFeedback.message}"
          </div>
        </div>
      )}

      {/* Boosts marketing (if any) */}
      {boostsUsed.length > 0 && (
        <div style={{
          background: '#252540',
          borderRadius: '8px',
          padding: '10px 15px',
          marginBottom: '15px',
          border: '1px dashed #cc00ff44',
          opacity: visibleCategories >= categories.length ? 1 : 0,
          transition: 'opacity 0.3s'
        }}>
          <div style={{ color: '#cc00ff', fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>
            📣 Boosts Marketing Actifs
          </div>
          <div style={{ color: '#888', fontSize: '11px' }}>
            {boostsUsed.join(' • ')}
          </div>
          <div style={{ color: '#00ff88', fontSize: '10px', marginTop: '5px' }}>
            ↳ Bonus appliqués aux downloads et abonnés
          </div>
        </div>
      )}

      {/* Trend Bonus (if any) */}
      {trendBonus && (
        <div style={{
          background: '#ff660022',
          borderRadius: '8px',
          padding: '10px 15px',
          marginBottom: '15px',
          border: '2px solid #ff6600',
          opacity: visibleCategories >= categories.length ? 1 : 0,
          transform: visibleCategories >= categories.length ? 'scale(1)' : 'scale(0.9)',
          transition: 'all 0.3s',
          animation: 'pulse 1s infinite'
        }}>
          <div style={{ color: '#ff6600', fontSize: '14px', fontWeight: 'bold' }}>
            🔥 SUJET TENDANCE !
          </div>
          <div style={{ color: '#ffaa00', fontSize: '12px', marginTop: '5px' }}>
            +50% Downloads & Abonnés
          </div>
        </div>
      )}

      {/* Ad Bonus (if any) */}
      {episodeResults.adBonus && (
        <div style={{
          background: '#ffcc0011',
          borderRadius: '8px',
          padding: '10px 15px',
          marginBottom: '15px',
          border: '2px solid #ffcc00',
          opacity: visibleCategories >= categories.length ? 1 : 0,
          transform: visibleCategories >= categories.length ? 'scale(1)' : 'scale(0.9)',
          transition: 'all 0.3s'
        }}>
          <div style={{
            color: '#ffcc00',
            fontSize: '14px',
            fontWeight: 'bold'
          }}>
            📢 PUBLICITÉS ({episodeResults.adPressure}%)
          </div>
          <div style={{
            color: '#ffaa00',
            fontSize: '12px',
            marginTop: '5px'
          }}>
            +{formatNumber(Math.floor(episodeResults.adBonus))}€ revenus supplémentaires
            <br />-{Math.floor(episodeResults.adPressure * 0.5)}% de conversion en abonnés
          </div>
        </div>
      )}

      {/* Final Score */}
      <div style={{
        background: '#252540',
        border: `3px solid ${showFinalScore ? getScoreColor(displayFinalScore) : '#333'}`,
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '20px',
        opacity: showFinalScore ? 1 : 0,
        transform: showFinalScore ? 'scale(1)' : 'scale(0.8)',
        transition: 'all 0.4s ease-out'
      }}>
        <div style={{ fontSize: '12px', color: '#888', marginBottom: '5px' }}>NOTE FINALE</div>
        <div style={{ 
          fontSize: '42px', 
          fontWeight: 'bold', 
          color: getScoreColor(displayFinalScore),
          transition: finalScoreLocked ? 'none' : 'color 0.05s'
        }}>
          {displayFinalScore.toFixed(1)}
        </div>
        <div style={{ fontSize: '16px', color: '#888' }}>/10</div>
        <div style={{ 
          fontSize: '22px', 
          marginTop: '5px', 
          color: getScoreColor(displayFinalScore), 
          letterSpacing: '3px'
        }}>
          {getStars(displayFinalScore)}
        </div>
      </div>

      {/* Gains */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '1fr 1fr', 
        gap: '10px',
        marginBottom: '15px',
        opacity: showGains ? 1 : 0,
        transform: showGains ? 'translateY(0)' : 'translateY(20px)',
        transition: 'all 0.3s ease-out'
      }}>
        <div style={{ 
          background: '#00ff8822', 
          padding: '12px', 
          borderRadius: '8px',
          border: '1px solid #00ff8844'
        }}>
          <div style={{ fontSize: '11px', color: '#00ff88' }}>DOWNLOADS</div>
          <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#00ff88' }}>
            +{formatNumber(episodeResults.downloads)}
          </div>
        </div>
        <div style={{ 
          background: '#00ccff22', 
          padding: '12px', 
          borderRadius: '8px',
          border: '1px solid #00ccff44'
        }}>
          <div style={{ fontSize: '11px', color: '#00ccff' }}>ABONNÉS</div>
          <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#00ccff' }}>
            +{episodeResults.subscribers}
          </div>
        </div>
      </div>

      <div style={{
        background: episodeResults.revenue >= 0 ? '#ffcc0022' : '#ff6b6b22',
        padding: '12px',
        borderRadius: '8px',
        marginBottom: '25px',
        border: `1px solid ${episodeResults.revenue >= 0 ? '#ffcc0044' : '#ff6b6b44'}`,
        opacity: showGains ? 1 : 0,
        transform: showGains ? 'translateY(0)' : 'translateY(20px)',
        transition: 'all 0.3s ease-out 0.1s'
      }}>
        <div style={{ fontSize: '11px', color: episodeResults.revenue >= 0 ? '#ffcc00' : '#ff6b6b' }}>PROFIT</div>
        <div style={{ fontSize: '24px', fontWeight: 'bold', color: episodeResults.revenue >= 0 ? '#ffcc00' : '#ff6b6b' }}>
          {episodeResults.revenue >= 0 ? '+' : ''}{episodeResults.revenue.toFixed(0)}€
        </div>
      </div>

      <button
        className="btn"
        onClick={onContinue}
        disabled={!showButton}
        style={{ 
          width: '100%', 
          padding: '15px',
          fontSize: '16px',
          borderColor: '#00ff88',
          color: '#00ff88',
          background: 'linear-gradient(135deg, #00ff8822, transparent)',
          opacity: showButton ? 1 : 0,
          transform: showButton ? 'translateY(0)' : 'translateY(10px)',
          transition: 'opacity 0.3s, transform 0.3s',
          cursor: showButton ? 'pointer' : 'default'
        }}
      >
        Continuer
      </button>
    </div>
  );
};

const UniversalPodcast = () => {
  // Load saved game state or use defaults
  // const loadGameState = () => {
  //   try {
  //     const saved = localStorage.getItem('universalPodcastSave');
  //     if (saved) {
  //       return JSON.parse(saved);
  //     }
  //   } catch (e) {
  //     console.error('Failed to load game:', e);
  //   }
  //   return null;
  // };

  // const savedState = loadGameState();
  const savedState = null; // localStorage disabled

  // ═══════════════════════════════════════════════════════════════
  // FORMATS & SUBJECTS DATA
  // ═══════════════════════════════════════════════════════════════
  
  const FORMATS = {
    solo: { name: '🎤 Solo', desc: 'Monologue personnel', unlockAt: 0 },
    interview: { name: '🎙️ Interview', desc: 'Discussion avec un invité', unlockAt: 0 },
    educatif: { name: '📚 Éducatif', desc: 'Tutoriel ou explication', unlockAt: 0 },
    storytelling: { name: '📖 Storytelling', desc: 'Narration immersive', unlockAt: 20000 },
    actualite: { name: '📰 Actualité', desc: 'Réaction à l\'actu', unlockAt: 350000 },
    debat: { name: '💬 Débat', desc: 'Confrontation d\'idées', unlockAt: 1000000 },
  };

  const SUBJECTS = {
    tech: { name: '💻 Tech', audience: 'young', unlockAt: 0 },
    culture: { name: '🎬 Culture Pop', audience: 'everyone', unlockAt: 0 },
    humour: { name: '😂 Humour', audience: 'everyone', unlockAt: 0 },
    gaming: { name: '🎮 Gaming', audience: 'young', unlockAt: 0 },
    business: { name: '💼 Business', audience: 'mature', unlockAt: 10000 },
    sport: { name: '⚽ Sport', audience: 'everyone', unlockAt: 25000 },
    science: { name: '🔬 Science', audience: 'mature', unlockAt: 100000 },
    truecrime: { name: '🔪 True Crime', audience: 'mature', unlockAt: 2000000 },
    societe: { name: '🌍 Société', audience: 'mature', unlockAt: 20000000 },
    lifestyle: { name: '💪 Lifestyle', audience: 'everyone', unlockAt: 35000000 },
  };

  // Combo matrix: format -> subject -> multiplier (0.5 = bad, 1.0 = neutral, 1.5 = great)
  const COMBO_MATRIX = {
    solo:        { tech: 1.2, business: 1.0, culture: 1.0, science: 1.0, sport: 0.8, truecrime: 1.5, humour: 1.3, societe: 1.2, gaming: 1.0, lifestyle: 1.3 },
    interview:   { tech: 1.5, business: 1.5, culture: 1.2, science: 1.3, sport: 1.2, truecrime: 1.0, humour: 1.0, societe: 1.4, gaming: 1.2, lifestyle: 1.2 },
    debat:       { tech: 1.0, business: 1.2, culture: 1.2, science: 1.0, sport: 1.3, truecrime: 0.7, humour: 1.5, societe: 1.5, gaming: 1.0, lifestyle: 0.8 },
    storytelling:{ tech: 0.7, business: 0.6, culture: 1.3, science: 1.0, sport: 0.7, truecrime: 1.5, humour: 1.0, societe: 1.2, gaming: 1.0, lifestyle: 1.0 },
    educatif:    { tech: 1.5, business: 1.4, culture: 0.8, science: 1.5, sport: 0.8, truecrime: 0.6, humour: 0.6, societe: 1.0, gaming: 1.3, lifestyle: 1.2 },
    actualite:   { tech: 1.2, business: 1.3, culture: 1.5, science: 1.0, sport: 1.4, truecrime: 1.0, humour: 1.2, societe: 1.4, gaming: 1.3, lifestyle: 0.9 },
  };

  // ═══════════════════════════════════════════════════════════════
  // TRENDING TOPICS SYSTEM (unlocked Phase 2)
  // ═══════════════════════════════════════════════════════════════
  
  const TREND_DURATION = 120;        // 2 minutes entre chaque rotation
  const TREND_COUNT = 1;             // 1 seule trend active à la fois
  const TREND_UNLOCK_EPISODES = 1;   // Débloqué après le 1er épisode (actif dès le 2ème)
  const TREND_BOOST_DL = 1.5;        // +50% downloads si trend
  const TREND_BOOST_SUBS = 1.5;      // +50% subs si trend
  
  const TRENDS = [
    // TECH
    {
      id: 'tech_ia',
      subjects: ['tech'],
      news: [
        "🤖 Une nouvelle IA fait le buzz sur les réseaux sociaux...",
        "💻 Apple dévoile son prochain produit révolutionnaire...",
        "🚀 Une start-up tech française lève 500 millions...",
        "📱 Le nouveau smartphone bat tous les records de ventes...",
      ]
    },
    {
      id: 'tech_cyber',
      subjects: ['tech'],
      news: [
        "🔐 Une faille de sécurité massive inquiète les experts...",
        "👾 Cyberattaque : des millions de données exposées...",
        "🛡️ Les hackers éthiques au cœur de l'actualité...",
      ]
    },
    
    // CULTURE POP
    {
      id: 'culture_cinema',
      subjects: ['culture'],
      news: [
        "🎬 Le film événement explose le box-office mondial...",
        "🏆 Surprise aux Oscars : le palmarès fait débat...",
        "🎥 Le réalisateur culte annonce son grand retour...",
      ]
    },
    {
      id: 'culture_musique',
      subjects: ['culture'],
      news: [
        "🎤 La tournée mondiale affiche complet en 10 minutes...",
        "🎵 L'album surprise affole les plateformes de streaming...",
        "🎸 Reformation surprise du groupe légendaire...",
      ]
    },
    
    // HUMOUR
    {
      id: 'humour_standup',
      subjects: ['humour'],
      news: [
        "😂 Le one-man-show fait scandale et cartonne...",
        "🎭 Les humoristes s'emparent des réseaux sociaux...",
        "📺 Le late show bat des records d'audience...",
      ]
    },
    {
      id: 'humour_viral',
      subjects: ['humour'],
      news: [
        "🤣 Le meme du moment envahit Internet...",
        "😅 La vidéo virale atteint 100 millions de vues...",
        "🃏 Le canular télévisé fait le tour du monde...",
      ]
    },
    
    // GAMING
    {
      id: 'gaming_release',
      subjects: ['gaming'],
      news: [
        "🎮 Le jeu le plus attendu de l'année sort enfin...",
        "🕹️ La nouvelle console next-gen fait fureur...",
        "👾 Le jeu indé surprise devient phénomène mondial...",
      ]
    },
    {
      id: 'gaming_esport',
      subjects: ['gaming'],
      news: [
        "🏆 L'équipe française remporte le mondial de League of Legends...",
        "🎯 Le tournoi Fortnite bat des records de viewers...",
        "💰 Le streamer Twitch signe un contrat record...",
      ]
    },
    
    // BUSINESS
    {
      id: 'business_startup',
      subjects: ['business'],
      news: [
        "💰 La licorne française valorisée 10 milliards...",
        "📈 L'entrepreneur prodige raconte son succès...",
        "🦄 La start-up qui révolutionne son secteur...",
      ]
    },
    {
      id: 'business_economie',
      subjects: ['business'],
      news: [
        "📊 Les marchés financiers en ébullition...",
        "🏦 La BCE surprend avec sa décision historique...",
        "💹 Crypto : le Bitcoin atteint un nouveau record...",
      ]
    },
    
    // SPORT
    {
      id: 'sport_foot',
      subjects: ['sport'],
      news: [
        "⚽ Transfert record : 200M€ pour la star mondiale...",
        "🏆 Victoire historique en finale de Champions League...",
        "🥅 L'équipe de France qualifiée pour le mondial...",
      ]
    },
    {
      id: 'sport_other',
      subjects: ['sport'],
      news: [
        "🎾 Sensation à Roland-Garros : le prodige triomphe...",
        "🏀 La NBA s'enflamme pour le rookie français...",
        "🏃 Record du monde pulvérisé aux championnats...",
        "🚴 Le Tour de France passionne les foules...",
      ]
    },
    
    // SCIENCE
    {
      id: 'science_space',
      subjects: ['science'],
      news: [
        "🚀 La NASA annonce une découverte majeure...",
        "🌍 La mission spatiale révèle des images inédites...",
        "👽 Signal mystérieux : les astronomes en émoi...",
        "🛸 SpaceX réussit son atterrissage historique...",
      ]
    },
    {
      id: 'science_discovery',
      subjects: ['science'],
      news: [
        "🔬 Une découverte médicale porteuse d'espoir...",
        "🧬 Révolution génétique : les chercheurs exultent...",
        "🦕 Un fossile exceptionnel découvert en France...",
        "🌡️ Climat : le rapport scientifique qui alarme...",
      ]
    },
    
    // TRUE CRIME
    {
      id: 'truecrime_affaire',
      subjects: ['truecrime'],
      news: [
        "🔍 Cold case résolu après 30 ans : rebondissement...",
        "⚖️ Le procès du siècle passionne l'opinion...",
        "🕵️ L'affaire non résolue refait surface...",
      ]
    },
    {
      id: 'truecrime_media',
      subjects: ['truecrime'],
      news: [
        "📺 La série documentaire Netflix cartonne...",
        "🎙️ Le podcast true crime dépasse 50M d'écoutes...",
        "📖 Le livre enquête devient best-seller...",
      ]
    },
    
    // SOCIÉTÉ
    {
      id: 'societe_debat',
      subjects: ['societe'],
      news: [
        "🏛️ Le débat sur la réforme enflamme le pays...",
        "📢 Manifestations : mobilisation historique...",
        "🗳️ Élections : retournement dans les sondages...",
      ]
    },
    {
      id: 'societe_phenomene',
      subjects: ['societe'],
      news: [
        "🌍 Le mouvement social prend une ampleur mondiale...",
        "📱 Le hashtag devient viral : des millions de réactions...",
        "👥 Phénomène de société : les experts analysent...",
      ]
    },
    
    // LIFESTYLE
    {
      id: 'lifestyle_wellness',
      subjects: ['lifestyle'],
      news: [
        "🧘 La tendance bien-être envahit les réseaux...",
        "💪 Le défi fitness devient viral sur TikTok...",
        "🥗 Le régime miracle : les nutritionnistes répondent...",
      ]
    },
    {
      id: 'lifestyle_tendance',
      subjects: ['lifestyle'],
      news: [
        "✨ La tendance lifestyle qui affole Instagram...",
        "🏠 Home organizing : le phénomène Marie Kondo 2.0...",
        "👔 Le style qui fait fureur cette saison...",
        "🌱 Slow life : le mouvement prend de l'ampleur...",
      ]
    },
  ];

  // ═══════════════════════════════════════════════════════════════
  // SPONSOR SYSTEM (Phase 3+)
  // ═══════════════════════════════════════════════════════════════
  
  // Constantes sponsors supprimées - remplacées par le système de pression publicitaire

  // Optimal slider ratios per format (must sum to 100)
  const OPTIMAL_RATIOS = {
    solo:        { prep: 30, perf: 50, prod: 20 },
    interview:   { prep: 40, perf: 40, prod: 20 },
    debat:       { prep: 25, perf: 55, prod: 20 },
    storytelling:{ prep: 50, perf: 20, prod: 30 },
    educatif:    { prep: 60, perf: 20, prod: 20 },
    actualite:   { prep: 25, perf: 35, prod: 40 },
  };

  // Feedback messages based on slider balance per format
  const SLIDER_FEEDBACK = {
    solo: {
      optimal: [
        "Tu as trouvé le bon équilibre ! Ton monologue était captivant.",
        "Bravo ! Tu maîtrises l'art du solo.",
        "Parfait dosage entre préparation et spontanéité !"
      ],
      lowPrep: [
        "Tu improvisais un peu trop... ça se sentait.",
        "Quelques moments de flottement, prépare mieux tes idées.",
      ],
      highPrep: [
        "Tu avais tout préparé... mais tu manquais de spontanéité.",
        "Trop scripté ! Un solo doit rester naturel.",
      ],
      lowPerf: [
        "Le contenu était bon, mais ton énergie manquait.",
        "Tu semblais fatigué... où était ta passion ?",
      ],
      highPerf: [
        "Beaucoup d'énergie ! Mais tu partais dans tous les sens.",
        "Tu t'emballais trop, on perdait le fil.",
      ],
      lowProd: [
        "De bons moments, mais le son était inégal par endroits.",
        "L'épisode aurait mérité un peu plus de polish au montage.",
      ],
      highProd: [
        "Le son est parfait ! Mais le contenu semblait un peu creux.",
        "Trop de temps sur la technique, pas assez sur le fond.",
      ]
    },
    interview: {
      optimal: [
        "Interview fluide et naturelle, ton invité était à l'aise !",
        "Excellente dynamique avec ton invité. Du grand art !",
        "Questions pertinentes, réponses captivantes. Parfait !"
      ],
      lowPrep: [
        "Tu ne connaissais pas assez ton invité, ça se voyait...",
        "Tes questions étaient un peu improvisées, non ?",
      ],
      highPrep: [
        "Interview trop rigide ! Laisse place à l'imprévu.",
        "Tu suivais ton script sans écouter les réponses.",
      ],
      lowPerf: [
        "L'invité parlait, parlait... et toi tu faisais quoi ?",
        "Ton invité s'ennuyait visiblement. Plus d'énergie !",
      ],
      highPerf: [
        "Tu parlais plus que ton invité ! C'est une interview, pas un solo.",
        "Trop d'interventions, laisse ton invité s'exprimer.",
      ],
      lowProd: [
        "On entendait mal ton invité par moments...",
        "Le montage aurait pu resserrer certains passages.",
      ],
      highProd: [
        "Le son était impeccable, mais la conversation manquait de vie.",
        "Techniquement parfait, humainement... bof.",
      ]
    },
    debat: {
      optimal: [
        "Débat animé mais respectueux. Bravo, arbitre !",
        "Tu as su créer de vraies confrontations d'idées !",
        "Excellent équilibre entre chaos et structure."
      ],
      lowPrep: [
        "Le débat partait dans tous les sens, faute de cadrage.",
        "Tu aurais dû mieux préparer les sujets de friction.",
      ],
      highPrep: [
        "Débat trop cadré ! Laisse les participants s'enflammer.",
        "On sentait que tout était prévu d'avance...",
      ],
      lowPerf: [
        "C'était un peu mou... un débat, ça doit avoir du peps !",
        "Où était la passion ? On s'endormait.",
      ],
      highPerf: [
        "C'était la foire d'empoigne ! Un peu de structure.",
        "Trop de cris, pas assez d'arguments.",
      ],
      lowProd: [
        "Difficile de suivre qui parlait avec ce montage.",
        "Le mix audio était confus, on perdait le fil.",
      ],
      highProd: [
        "Techniquement clean, mais le débat manquait de flamme.",
        "Beau son, mais où étaient les étincelles ?",
      ]
    },
    storytelling: {
      optimal: [
        "Immersion totale ! On était scotchés.",
        "Narration magnifique, on en redemande !",
        "Tu nous as transportés dans ton histoire."
      ],
      lowPrep: [
        "L'histoire avait des trous, tu improvisais trop.",
        "Le scénario manquait de structure narrative.",
      ],
      highPrep: [
        "Histoire trop prévisible, on voyait les ficelles.",
        "Récit trop mécanique, il manquait de la magie.",
      ],
      lowPerf: [
        "Belle histoire, mais ta voix manquait d'émotion.",
        "Le texte était bon, la lecture... moins.",
      ],
      highPerf: [
        "Tu en faisais trop ! Le storytelling, c'est subtil.",
        "Surjoué. Laisse l'histoire parler d'elle-même.",
      ],
      lowProd: [
        "Il manquait des ambiances sonores pour l'immersion.",
        "Le montage aurait pu renforcer les moments clés.",
      ],
      highProd: [
        "Super production ! Mais l'histoire elle-même...",
        "Effets sonores au top, narration en berne.",
      ]
    },
    educatif: {
      optimal: [
        "Clair, précis, pédagogue. Tu es un vrai prof !",
        "J'ai tout compris du premier coup. Bravo !",
        "Explication limpide et captivante."
      ],
      lowPrep: [
        "Tu as expliqué n'importe quoi par moments...",
        "Quelques approximations qui font tache. Vérifie tes sources !",
      ],
      highPrep: [
        "Trop de détails ! Tu as noyé l'essentiel.",
        "C'était un cours magistral, pas un podcast accessible.",
      ],
      lowPerf: [
        "Bon contenu, mais tu parlais de façon monotone.",
        "Les infos étaient là, pas l'enthousiasme.",
      ],
      highPerf: [
        "Tu étais enthousiaste ! Mais on a rien retenu.",
        "Moins d'excitation, plus de pédagogie.",
      ],
      lowProd: [
        "Des schémas audio auraient aidé à comprendre.",
        "Le montage aurait pu mieux structurer l'explication.",
      ],
      highProd: [
        "Joli habillage, mais le fond était confus.",
        "Bien produit, mal expliqué.",
      ]
    },
    actualite: {
      optimal: [
        "Réactif, pertinent, bien sourcé. Du bon journalisme !",
        "Tu as su décrypter l'actu avec brio.",
        "Analyse pointue et accessible. Parfait !"
      ],
      lowPrep: [
        "Tes infos n'étaient pas à jour...",
        "Tu as réagi à chaud sans vérifier les faits.",
      ],
      highPrep: [
        "Trop d'analyse, plus assez d'actualité !",
        "Le temps que tu te prépares, l'info était dépassée.",
      ],
      lowPerf: [
        "L'actu, c'est du rythme ! Tu étais trop lent.",
        "Plus de punch, c'est de l'actualité !",
      ],
      highPerf: [
        "Tu allais trop vite, on n'a rien compris !",
        "Calme-toi, ce n'est pas une course.",
      ],
      lowProd: [
        "L'habillage sonore 'breaking news' manquait.",
        "Le montage était trop lent pour de l'actu.",
      ],
      highProd: [
        "Format pro, mais le contenu était light.",
        "Ça sonnait bien, mais tu n'as rien dit de neuf.",
      ]
    }
  };

  // Function to get slider feedback
  const getSliderFeedback = (format, prep, perf, prod) => {
    const optimal = OPTIMAL_RATIOS[format];
    const feedback = SLIDER_FEEDBACK[format];
    if (!optimal || !feedback) return null;

    const tolerance = 15; // ±15% tolerance before criticism
    const prepDiff = prep - optimal.prep;
    const perfDiff = perf - optimal.perf;
    const prodDiff = prod - optimal.prod;

    // Check if close to optimal
    const isOptimal = Math.abs(prepDiff) <= tolerance && 
                      Math.abs(perfDiff) <= tolerance && 
                      Math.abs(prodDiff) <= tolerance;

    if (isOptimal) {
      return {
        type: 'success',
        message: feedback.optimal[Math.floor(Math.random() * feedback.optimal.length)],
        hint: null
      };
    }

    // Find the biggest problem
    const problems = [];
    
    // Low values (not enough)
    if (prepDiff < -tolerance) problems.push({ type: 'lowPrep', diff: Math.abs(prepDiff), hint: 'Plus de Préparation' });
    if (perfDiff < -tolerance) problems.push({ type: 'lowPerf', diff: Math.abs(perfDiff), hint: 'Plus de Performance' });
    if (prodDiff < -tolerance) problems.push({ type: 'lowProd', diff: Math.abs(prodDiff), hint: 'Plus de Production' });
    
    // High values (too much)
    if (prepDiff > tolerance) problems.push({ type: 'highPrep', diff: Math.abs(prepDiff), hint: 'Moins de Préparation' });
    if (perfDiff > tolerance) problems.push({ type: 'highPerf', diff: Math.abs(perfDiff), hint: 'Moins de Performance' });
    if (prodDiff > tolerance) problems.push({ type: 'highProd', diff: Math.abs(prodDiff), hint: 'Moins de Production' });

    if (problems.length === 0) {
      return {
        type: 'success',
        message: feedback.optimal[Math.floor(Math.random() * feedback.optimal.length)],
        hint: null
      };
    }

    // Get the worst problem
    problems.sort((a, b) => b.diff - a.diff);
    const worst = problems[0];
    const messages = feedback[worst.type];

    return {
      type: 'warning',
      message: messages[Math.floor(Math.random() * messages.length)],
      hint: worst.hint
    };
  };

  // Core resources
  const [downloads, setDownloads] = useState(savedState?.downloads || 0);
  const [episodes, setEpisodes] = useState(savedState?.episodes || 0);
  const [subscribers, setSubscribers] = useState(savedState?.subscribers || 0);
  const [money, setMoney] = useState(savedState?.money || 300); // Start with 300€
  const [catalogHype, setCatalogHype] = useState(savedState?.catalogHype || 0); // Hype system for passive income decay
  
  // Episode creation state
  const [selectedFormat, setSelectedFormat] = useState('solo');
  const [selectedSubject, setSelectedSubject] = useState('tech');
  const [sliderPrep, setSliderPrep] = useState(33);
  const [sliderPerf, setSliderPerf] = useState(34);
  const [sliderProd, setSliderProd] = useState(33);
  const [lastCombo, setLastCombo] = useState(null);
  const [showCreator, setShowCreator] = useState(false);
  const [creatorStep, setCreatorStep] = useState(1); // 1=concept, 2=sliders, 3=equipment, 4=boost, 5=recording, 6=results
  const [episodeResults, setEpisodeResults] = useState(null); // Frozen results for display
  
  // Episode boost choices (reset each episode)
  const [episodeBoosts, setEpisodeBoosts] = useState({
    socialAds: false,      // 50€ - +20% downloads
    guestStar: false,      // 100€ - +30% downloads, +50% subs
    proCover: false,       // 30€ - +10% downloads
    crossPromo: false,     // 80€ - +25% subs
    newsletter: false      // 20€ - +15% subs
  });
  
  // Permanent equipment upgrades
  const MIC_UPGRADES = [
    { name: '🎤 Micro USB', bonus: 1.0, cost: 0, desc: 'Basique mais fonctionnel' },
    { name: '🎙️ Micro XLR', bonus: 1.15, cost: 150, desc: 'Qualité pro' },
    { name: '🏛️ Studio Pro', bonus: 1.3, cost: 1500, desc: 'Son broadcast' }
  ];
  
  const EDITING_UPGRADES = [
    { name: '✂️ Soi-même', bonus: 1.0, cost: 0, desc: 'Gratuit mais amateur' },
    { name: '👨‍💻 Freelance', bonus: 1.1, cost: 100, desc: 'Montage propre' },
    { name: '🏢 Agence', bonus: 1.25, cost: 1000, desc: 'Production premium' }
  ];
  
  const BOOSTS = {
    socialAds: { name: '📱 Pub Réseaux', dlBonus: 0.50, subBonus: 0 },
    guestStar: { name: '⭐ Invité Star', dlBonus: 0.75, subBonus: 1.00 },
    proCover: { name: '🎨 Cover Pro', dlBonus: 0.25, subBonus: 0 },
    crossPromo: { name: '🤝 Cross-Promo', dlBonus: 0, subBonus: 0.50 },
    newsletter: { name: '📧 Newsletter', dlBonus: 0, subBonus: 0.30 }
  };

  // Prix des boosts par phase (scalés)
  const BOOST_PHASE_PRICES = {
    newsletter:  { 1: 5,  2: 15,  3: 25,  4: 100,  5: 250,  6: 500 },
    proCover:    { 1: 10, 2: 25,  3: 50,  4: 200,  5: 500,  6: 1000 },
    socialAds:   { 1: 15, 2: 40,  3: 75,  4: 300,  5: 750,  6: 1500 },
    crossPromo:  { 1: 20, 2: 50,  3: 100, 4: 400,  5: 1000, 6: 2000 },
    guestStar:   { 1: 30, 2: 75,  3: 150, 4: 600,  5: 1500, 6: 3000 }
  };

  // Helper pour obtenir le prix d'un boost selon la phase actuelle
  const getBoostCost = (boostKey) => BOOST_PHASE_PRICES[boostKey]?.[phase] || 0;
  
  // Production
  // Production - permanent equipment upgrades
  const [autoProducers, setAutoProducers] = useState(savedState?.autoProducers || 0);
  const autoEpisodeAccumRef = useRef(0); // Accumulator for fractional episodes
  const [micLevel, setMicLevel] = useState(savedState?.micLevel || 0); // 0=USB, 1=XLR, 2=Studio
  const [editingLevel, setEditingLevel] = useState(savedState?.editingLevel || 0); // 0=Self, 1=Freelance, 2=Agency
  
  // Marketing
  const [seoLevel, setSeoLevel] = useState(savedState?.seoLevel || 0);
  const [socialBots, setSocialBots] = useState(savedState?.socialBots || 0);
  
  // CPM fixe à 20€
  const cpm = 20;
  
  // Advanced
  const [aiHosts, setAiHosts] = useState(savedState?.aiHosts || 0);
  const [podcastNetworks, setPodcastNetworks] = useState(savedState?.podcastNetworks || 0);
  const [universeConversion, setUniverseConversion] = useState(savedState?.universeConversion || 0);

  // End Game Sequence
  const [endingSequenceStep, setEndingSequenceStep] = useState(0);
  const [isEndingActive, setIsEndingActive] = useState(false);
  const gameStartTimeRef = useRef(Date.now());
  const finalDownloadsRef = useRef(0);

  // Phase 5 - Domination Nationale
  const [internationalLevel, setInternationalLevel] = useState(savedState?.internationalLevel || 0);
  const [radioLevel, setRadioLevel] = useState(savedState?.radioLevel || 0);
  const [tvLevel, setTvLevel] = useState(savedState?.tvLevel || 0);
  const [lobbyingLevel, setLobbyingLevel] = useState(savedState?.lobbyingLevel || 0);
  
  // Phase 6 - Singularité
  const [mindControlLevel, setMindControlLevel] = useState(savedState?.mindControlLevel || 0);
  const [spaceLevel, setSpaceLevel] = useState(savedState?.spaceLevel || 0);
  
  // Trending Topics (Phase 2+)
  const [activeTrends, setActiveTrends] = useState(savedState?.activeTrends || []);
  const [trendTimer, setTrendTimer] = useState(savedState?.trendTimer || TREND_DURATION);
  
  // Pression Publicitaire (Phase 3+)
  const [adPressure, setAdPressure] = useState(savedState?.adPressure ?? 0); // 0-100%
  
  // Game state
  const [phase, setPhase] = useState(savedState?.phase || 1);
  const [showDevPanel, setShowDevPanel] = useState(false); // DEV MODE
  const [log, setLog] = useState(savedState?.log || ['🎙️ Tu démarres ton podcast avec un micro USB et 300€ d\'économies. Tu n\'as encore aucun abonné...']);
  const [unlockedMilestones, setUnlockedMilestones] = useState(savedState?.unlockedMilestones || []);
  
  // ═══════════════════════════════════════════════════════════════
  // MILESTONE SYSTEM - Progressive unlocks
  // ═══════════════════════════════════════════════════════════════
  
  const MILESTONES = [
    // Phase 1: Podcaster Amateur (0 → 50k DL)
    { id: 'mic_xlr', type: 'downloads', threshold: 500, unlock: 'upgrade_mic_xlr', message: '🎙️ Micro XLR disponible !' },
    { id: 'boost_newsletter', type: 'downloads', threshold: 2000, unlock: 'boost_newsletter', message: '📧 Boost Newsletter débloqué !' },
    { id: 'boost_cover', type: 'downloads', threshold: 5000, unlock: 'boost_proCover', message: '🎨 Boost Cover Pro débloqué !' },
    { id: 'editing_freelance', type: 'downloads', threshold: 10000, unlock: 'upgrade_editing_freelance', message: '✂️ Éditeur Freelance disponible !' },
    { id: 'format_story', type: 'downloads', threshold: 20000, unlock: 'format_storytelling', message: '📖 Nouveau format : Storytelling !' },
    { id: 'boost_social', type: 'downloads', threshold: 35000, unlock: 'boost_socialAds', message: '📱 Boost Pub Réseaux débloqué !' },
    { id: 'phase_2', type: 'downloads', threshold: 50000, unlock: 'phase_2', message: '🎯 PHASE 2 : Podcaster Pro ! De nouveaux outils s\'offrent à toi.', phase: 2 },
    
    // Phase 2: Podcaster Pro (50k → 500k DL)
    { id: 'studio_1', type: 'downloads', threshold: 50000, unlock: 'upgrade_studio', message: '🎚️ Studio disponible !' },
    { id: 'boost_guest', type: 'downloads', threshold: 75000, unlock: 'boost_guestStar', message: '⭐ Boost Invité Star débloqué !' },
    { id: 'pso_1', type: 'downloads', threshold: 100000, unlock: 'upgrade_pso', message: '📈 PSO (Podcast SEO) disponible !' },
    { id: 'mic_studio', type: 'downloads', threshold: 150000, unlock: 'upgrade_mic_studio', message: '🎙️ Micro Studio disponible !' },
    { id: 'boost_cross', type: 'downloads', threshold: 250000, unlock: 'boost_crossPromo', message: '🤝 Boost Cross-Promo débloqué !' },
    { id: 'format_actualite', type: 'downloads', threshold: 350000, unlock: 'format_actualite', message: '📰 Nouveau format : Actualité !' },
    { id: 'editing_agency', type: 'downloads', threshold: 400000, unlock: 'upgrade_editing_agency', message: '✂️ Agence Audio disponible !' },
    { id: 'phase_3', type: 'downloads', threshold: 500000, unlock: 'phase_3', message: '🏢 PHASE 3 : Studio Indépendant ! L\'automatisation devient possible.', phase: 3 },
    
    // Phase 3: Studio Indépendant (500k → 5M DL)
    { id: 'assistant_1', type: 'downloads', threshold: 500000, unlock: 'upgrade_assistant', message: '👨‍💻 Assistant Production disponible !' },
    { id: 'social_bots', type: 'downloads', threshold: 750000, unlock: 'upgrade_socialbots', message: '🤖 Bots Réseaux Sociaux disponibles !' },
    { id: 'format_debat', type: 'downloads', threshold: 1000000, unlock: 'format_debat', message: '💬 Nouveau format : Débat !' },
    { id: 'subject_truecrime', type: 'downloads', threshold: 2000000, unlock: 'subject_truecrime', message: '🔪 Nouveau sujet : True Crime !' },
    { id: 'milestone_3_5m', type: 'downloads', threshold: 3500000, unlock: 'milestone_3_5m', message: '📊 3.5M téléchargements ! Ta notoriété grandit.' },
    { id: 'phase_4', type: 'downloads', threshold: 5000000, unlock: 'phase_4', message: '🚀 PHASE 4 : Empire Médiatique ! Les réseaux de podcasts t\'ouvrent leurs portes.', phase: 4 },
    
    // Phase 4: Empire Médiatique (5M → 50M DL)
    { id: 'network_1', type: 'downloads', threshold: 7500000, unlock: 'upgrade_network', message: '🌐 Réseau de Podcasts disponible !' },
    { id: 'ai_host', type: 'downloads', threshold: 10000000, unlock: 'upgrade_aihost', message: '🤖 Hôte IA disponible !' },
    { id: 'subject_societe', type: 'downloads', threshold: 20000000, unlock: 'subject_societe', message: '🌍 Nouveau sujet : Société !' },
    { id: 'subject_lifestyle', type: 'downloads', threshold: 35000000, unlock: 'subject_lifestyle', message: '💪 Nouveau sujet : Lifestyle !' },
    { id: 'phase_5', type: 'downloads', threshold: 50000000, unlock: 'phase_5', message: '🇫🇷 PHASE 5 : Domination Nationale ! L\'international t\'attend.', phase: 5 },
    
    // Phase 5: Domination Nationale (50M → 500M DL)
    { id: 'international', type: 'downloads', threshold: 50000000, unlock: 'upgrade_international', message: '🌍 Expansion Internationale disponible !' },
    { id: 'radio', type: 'downloads', threshold: 75000000, unlock: 'upgrade_radio', message: '📡 Chaîne Radio disponible !' },
    { id: 'tv', type: 'downloads', threshold: 100000000, unlock: 'upgrade_tv', message: '🎬 Adaptation TV disponible !' },
    { id: 'lobbying', type: 'downloads', threshold: 200000000, unlock: 'upgrade_lobbying', message: '🏛️ Lobbying Culturel disponible !' },
    { id: 'milestone_350m', type: 'downloads', threshold: 350000000, unlock: 'milestone_350m', message: '📡 350M téléchargements ! Tu domines les ondes.' },
    { id: 'phase_6', type: 'downloads', threshold: 500000000, unlock: 'phase_6', message: '🌌 PHASE 6 : Singularité Audio ! Tu transcendes le medium.', phase: 6 },
    
    // Phase 6: Singularité Audio (500M+ DL)
    { id: 'mind_control', type: 'downloads', threshold: 500000000, unlock: 'upgrade_mindcontrol', message: '🧠 Contrôle Mental disponible !' },
    { id: 'space', type: 'downloads', threshold: 1000000000, unlock: 'upgrade_space', message: '🛸 Diffusion Spatiale disponible !' },
    { id: 'universe', type: 'downloads', threshold: 5000000000, unlock: 'upgrade_universe', message: '🌌 Convertisseur Universel disponible !' },
    { id: 'game_complete', type: 'downloads', threshold: 8000000000, unlock: 'game_complete', message: '🌍 TOUTE L\'HUMANITÉ A ÉCOUTÉ. L\'UNIVERS EST DEVENU PODCAST.' }
  ];
  
  const phaseNames = {
    1: 'PODCASTER AMATEUR',
    2: 'PODCASTER PRO', 
    3: 'STUDIO INDÉPENDANT',
    4: 'EMPIRE MÉDIATIQUE',
    5: 'DOMINATION NATIONALE',
    6: 'SINGULARITÉ AUDIO'
  };
  
  // Helper function to check if an upgrade/boost is unlocked
  const isUnlocked = (unlockId) => {
    const milestone = MILESTONES.find(m => m.unlock === unlockId);
    if (!milestone) return true; // If no milestone required, always available
    return unlockedMilestones.includes(milestone.id);
  };
  
  // Debug panel state
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [debugScore, setDebugScore] = useState(7);
  const [debugBoosts, setDebugBoosts] = useState({
    socialAds: false,
    guestStar: false,
    proCover: false,
    crossPromo: false,
    newsletter: false
  });

  // ═══════════════════════════════════════════════════════════════
  // EPISODE QUALITY SYSTEM
  // ═══════════════════════════════════════════════════════════════
  
  // Get unlocked formats based on total downloads
  const unlockedFormats = Object.entries(FORMATS)
    .filter(([key, format]) => downloads >= format.unlockAt)
    .map(([key]) => key);
  
  // Get unlocked subjects based on total downloads
  const unlockedSubjects = Object.entries(SUBJECTS)
    .filter(([key, subject]) => downloads >= subject.unlockAt)
    .map(([key]) => key);
  
  // Calculate combo score (format × subject compatibility)
  const getComboScore = (format, subject) => {
    return COMBO_MATRIX[format]?.[subject] || 1.0;
  };
  
  // Calculate slider efficiency (how close to optimal)
  const getSliderEfficiency = (format, prep, perf, prod) => {
    const optimal = OPTIMAL_RATIOS[format];
    if (!optimal) return 1.0;
    
    // Calculate deviation from optimal (0 = perfect, higher = worse)
    const prepDiff = Math.abs(prep - optimal.prep);
    const perfDiff = Math.abs(perf - optimal.perf);
    const prodDiff = Math.abs(prod - optimal.prod);
    const totalDiff = prepDiff + perfDiff + prodDiff;
    
    // Convert to efficiency (0 diff = 1.0, 200 diff = 0.1)
    // Min 0.1 to avoid divide by zero issues
    const efficiency = Math.max(0.1, 1 - (totalDiff / 200));
    return efficiency;
  };
  
  // Get combo rating text
  const getComboRating = (score) => {
    if (score >= 1.4) return { text: 'EXCELLENT', stars: '⭐⭐⭐⭐⭐', color: '#00ff88' };
    if (score >= 1.2) return { text: 'TRÈS BON', stars: '⭐⭐⭐⭐', color: '#00ffcc' };
    if (score >= 1.0) return { text: 'BON', stars: '⭐⭐⭐', color: '#ffcc00' };
    if (score >= 0.8) return { text: 'MOYEN', stars: '⭐⭐', color: '#ff9900' };
    return { text: 'MAUVAIS', stars: '⭐', color: '#ff6b6b' };
  };
  
  // Current episode quality calculation
  const currentCombo = `${selectedFormat}-${selectedSubject}`;
  const comboScore = getComboScore(selectedFormat, selectedSubject);
  const sliderEfficiency = getSliderEfficiency(selectedFormat, sliderPrep, sliderPerf, sliderProd);
  const repeatPenalty = lastCombo === currentCombo ? 0.7 : 1.0;
  
  // ARTISTIC SCORE (capped at 2.0 for display as /10)
  const artisticMultiplier = Math.min(2.0, comboScore * sliderEfficiency * repeatPenalty);
  const displayScore = Math.min(10, artisticMultiplier * 5).toFixed(1);
  const comboRating = getComboRating(comboScore * sliderEfficiency);
  
  // EQUIPMENT BONUSES (from permanent upgrades)
  const micBonus = MIC_UPGRADES[micLevel]?.bonus || 1.0;
  const editingBonus = EDITING_UPGRADES[editingLevel]?.bonus || 1.0;
  const equipmentMultiplier = micBonus * editingBonus;
  
  // BOOST BONUSES (per episode)
  const boostDlMultiplier = 1 + Object.entries(episodeBoosts)
    .filter(([_, active]) => active)
    .reduce((sum, [key]) => sum + (BOOSTS[key]?.dlBonus || 0), 0);
  const boostSubMultiplier = 1 + Object.entries(episodeBoosts)
    .filter(([_, active]) => active)
    .reduce((sum, [key]) => sum + (BOOSTS[key]?.subBonus || 0), 0);
  
  // TOTAL COSTS for this episode (only boosts now - scaled by phase)
  const boostCost = Object.entries(episodeBoosts)
    .filter(([_, active]) => active)
    .reduce((sum, [key]) => sum + getBoostCost(key), 0);
  
  // Production cost scales with phase (6 phases)
  const PHASE_COSTS = { 1: 20, 2: 50, 3: 100, 4: 250, 5: 500, 6: 1000 };
  const productionCost = PHASE_COSTS[phase] || 20;
  
  // Lobbying: -25% production cost per level (min 50% cost)
  const lobbyingDiscount = Math.max(0.5, 1 - (lobbyingLevel * 0.25));
  const effectiveProductionCost = Math.floor(productionCost * lobbyingDiscount);
  
  const totalEpisodeCost = effectiveProductionCost + boostCost;
  
  // ═══════════════════════════════════════════════════════════════
  // SCORE-BASED SYSTEM - Note finale pilote les résultats
  // ═══════════════════════════════════════════════════════════════
  
  // Calculate individual scores (1-10 scale)
  const conceptScoreRaw = Math.min(10, Math.max(1, (comboScore - 0.5) * 10));
  const conceptScoreFinal = conceptScoreRaw * repeatPenalty; // Apply repeat penalty
  
  const balanceScore = Math.min(10, Math.max(1, sliderEfficiency * 10));
  
  // Equipment: base (1.0) = 1/10, max (1.625) = 10/10
  const maxEquipMultiplier = 1.625; // 1.3 * 1.25
  const equipScore = Math.min(10, Math.max(1, ((equipmentMultiplier - 1) / (maxEquipMultiplier - 1)) * 9 + 1));
  
  // FINAL SCORE (weighted average) - 3 catégories seulement
  // Concept 40% + Enregistrement 40% + Équipement 20%
  const finalScore = (conceptScoreFinal * 0.40 + balanceScore * 0.40 + equipScore * 0.20);
  
  // Score multiplier: note 5 = ×1, note 10 = ×2, note 1 = ×0.2
  const scoreMultiplier = finalScore / 5;
  
  // ═══════════════════════════════════════════════════════════════
  // DOWNLOADS & REVENUE - Based on final score + boosts
  // ═══════════════════════════════════════════════════════════════
  
  // BASE DOWNLOADS calculation (before score and boosts)
  const baseListens = 200;
  const seoMultiplier = 1 + seoLevel * 0.5; // PSO booste les subs, pas les DL
  const subscriberBase = Math.floor(subscribers * 0.7);
  const viralityBonus = subscribers > 100 ? Math.floor(Math.sqrt(subscribers) * 0.5) : 0;
  const baseDownloadsPerEpisode = Math.floor(baseListens + subscriberBase + viralityBonus);

  // SCORE-ADJUSTED downloads with boosts
  const qualityDownloadsPerEpisode = Math.floor(baseDownloadsPerEpisode * scoreMultiplier * boostDlMultiplier);
  
  // For auto-production (uses equipment score only, no boosts)
  // For auto-production (uses equipment bonus directly, not score)
  const autoScoreMultiplier = equipmentMultiplier; // 1.0 → 1.15 → 1.625 max
  const downloadsPerEpisode = Math.floor(baseDownloadsPerEpisode * autoScoreMultiplier);
  
  // CATALOG HYPE SYSTEM - Passive downloads based on hype (decays over time)
  // Hype floor = minimum hype based on total episodes (SEO "longue traîne")
  const hypeFloor = episodes * 5;
  const effectiveHype = Math.max(catalogHype, hypeFloor);
  const maxHype = Math.max(50, Math.floor(500 * Math.log10(episodes + 1))); // Logarithmic scaling
  const hypePercentage = maxHype > 0 ? Math.min(100, (effectiveHype / maxHype) * 100) : 0;
  
  // CATALOG (inventory) - Episodes generate passive downloads based on HYPE
  const baseCatalogDownloads = Math.floor(effectiveHype * subscribers * 0.001);
  const catalogDownloadsPerSecond = Math.floor(baseCatalogDownloads * autoScoreMultiplier);
  
  // FILL RATE - % of downloads that are monetized (depends on audience size)
  // Fill rate fixe à 50%
  const fillRate = 0.50;
  
  // REVENUE = Downloads × CPM / 1000 × Fill Rate
  const revenuePerDownload = (cpm / 1000) * fillRate; // Effective revenue after fill rate
  
  // Revenue calculations (productionCost already included in totalEpisodeCost)
  const totalRevenuePerEpisode = (downloadsPerEpisode * revenuePerDownload) - productionCost;
  const qualityRevenuePerEpisode = (qualityDownloadsPerEpisode * revenuePerDownload) - totalEpisodeCost;
  
  // Subscriber conversion rate (10% base, affected by score AND boosts AND PSO)
  const baseSubConversion = 0.10; // 10% base (was 5%)
  const qualitySubConversion = baseSubConversion * scoreMultiplier * boostSubMultiplier * seoMultiplier;
  
  // ═══════════════════════════════════════════════════════════════
  // PHASE 5 & 6 MULTIPLIERS
  // ═══════════════════════════════════════════════════════════════
  
  // International expansion: ×2 audience per level
  const internationalMultiplier = Math.pow(2, internationalLevel);
  
  // TV: ×1.5 hype multiplier per level
  const tvHypeMultiplier = Math.pow(1.5, tvLevel);
  
  // Auto production
  const autoProductionRate = autoProducers * editingBonus * 0.1 + aiHosts * 0.1;
  
  // Subscriber growth rate (includes Phase 5/6 boosts)
  const baseSubGrowth = (downloads > 0 ? Math.log10(downloads + 1) * 0.1 : 0);
  // Bots sociaux: chaque bot ajoute 1 × 1.8^(index-1) subs/sec (scaling)
  // Bot 1: 1/sec, Bot 2: +1.8/sec (total 2.8), Bot 3: +3.24/sec (total 6.04)...
  // Pression pub réduit l'efficacité des bots (trop de pub = les gens fuient)
  const adBotPenalty = 1 - (adPressure * 0.005); // 0% = ×1, 100% = ×0.5
  const botSubGrowth = socialBots > 0 ? ((Math.pow(1.8, socialBots) - 1) / 0.8) * adBotPenalty : 0;
  const networkSubGrowth = podcastNetworks * 10;
  const tvSubGrowth = tvLevel * 100;
  const mindControlSubGrowth = mindControlLevel * 1000;
  const subscriberGrowthRate = (baseSubGrowth + botSubGrowth + networkSubGrowth + tvSubGrowth + mindControlSubGrowth) * internationalMultiplier;
  
  // Passive revenue (includes radio)
  const radioRevenuePerSecond = radioLevel * 500;
  
  // Space downloads (Phase 6)
  const spaceDownloadsPerSecond = spaceLevel * 1000000;
  
  // Revenue per second (from catalog + radio) avec bonus pub
  const adRevenueMultiplier = 1 + (adPressure / 100); // 0% = ×1, 100% = ×2
  const catalogRevenuePerSecond = catalogDownloadsPerSecond * revenuePerDownload * adRevenueMultiplier;
  const revenuePerSecond = catalogRevenuePerSecond + radioRevenuePerSecond;
  
  // Add log message
  const addLog = useCallback((message) => {
    setLog(prev => [...prev.slice(-19), message]);
  }, []);

  // Rotate trending topics (Phase 2+)
  const rotateTrends = useCallback(() => {
    // Exclure les trends actuellement actives
    const currentIds = activeTrends.map(t => t.id);
    const available = TRENDS.filter(t => !currentIds.includes(t.id));
    
    // Mélanger et prendre TREND_COUNT trends
    const shuffled = [...available].sort(() => Math.random() - 0.5);
    const newTrends = shuffled.slice(0, TREND_COUNT);
    
    setActiveTrends(newTrends);
    
    // Afficher une news pour chaque trend dans le log
    newTrends.forEach(trend => {
      const randomNews = trend.news[Math.floor(Math.random() * trend.news.length)];
      addLog(randomNews);
    });
  }, [activeTrends, addLog]);

  // Format number helper
  const formatNumber = useCallback((n) => {
    if (n >= 1e15) return (n / 1e15).toFixed(2) + ' Q';
    if (n >= 1e12) return (n / 1e12).toFixed(2) + ' T';
    if (n >= 1e9) return (n / 1e9).toFixed(2) + ' B';
    if (n >= 1e6) return (n / 1e6).toFixed(2) + ' M';
    if (n >= 1e3) return (n / 1e3).toFixed(2) + ' K';
    return Math.floor(n).toString();
  }, []);
  
  // Format avec séparateurs d'espaces pour les grands nombres
  const formatNumberFull = useCallback((n) => {
    return Math.floor(n).toLocaleString('fr-FR').replace(/,/g, ' ');
  }, []);

  // Record an episode manually (with quality system)
  const recordEpisode = () => {
    // Check if enough money for production
    if (money < productionCost) {
      addLog(`⚠️ Pas assez d'argent ! Besoin de ${productionCost}€ pour produire.`);
      return;
    }
    
    // Check if subject matches an active trend (Phase 2+)
    let trendMultiplierDL = 1.0;
    let trendMultiplierSubs = 1.0;
    let matchedTrend = null;
    
    if (episodes >= TREND_UNLOCK_EPISODES && activeTrends.length > 0) {
      matchedTrend = activeTrends.find(trend => 
        trend.subjects.includes(selectedSubject)
      );
      
      if (matchedTrend) {
        trendMultiplierDL = TREND_BOOST_DL;
        trendMultiplierSubs = TREND_BOOST_SUBS;
      }
    }
    
    // PRESSION PUBLICITAIRE (Phase 3+)
    // Plus de pub = plus de revenus par écoute, mais moins d'abonnés
    const adRevenueMultiplier = 1 + (adPressure / 100); // 0% = ×1, 100% = ×2
    const adSubPenalty = 1 - (adPressure * 0.005); // 0% = ×1, 100% = ×0.5

    const adNote = adPressure > 0 ? ` 📢 Pub ${adPressure}%` : '';
    
    // Calculate final episode stats with quality, trend, and ad pressure
    const finalDownloads = Math.floor(qualityDownloadsPerEpisode * trendMultiplierDL);
    const finalSubs = Math.floor(finalDownloads * qualitySubConversion * trendMultiplierSubs * adSubPenalty);
    const baseRevenue = finalDownloads * revenuePerDownload;
    const finalRevenue = baseRevenue * adRevenueMultiplier;
    const adBonus = finalRevenue - baseRevenue;
    
    // Apply changes
    setEpisodes(e => e + 1);
    setDownloads(d => d + finalDownloads);
    setSubscribers(s => s + finalSubs);
    setMoney(m => m + finalRevenue);
    setLastCombo(currentCombo);
    setShowCreator(false);
    
    // Plus de logique de contrats - système simplifié
    
    // Format names for log
    const formatName = FORMATS[selectedFormat]?.name || selectedFormat;
    const subjectName = SUBJECTS[selectedSubject]?.name || selectedSubject;
    const qualityStars = comboRating.stars;
    const profitEmoji = finalRevenue > 0 ? '💰' : '💸';
    const repeatWarning = repeatPenalty < 1 ? ' ⚠️ Répétition!' : '';
    const trendNote = matchedTrend ? ' 🔥 TREND!' : '';
    const adPenaltyNote = adPressure > 50 ? ` ⚠️ Pub forte` : '';

    addLog(`🎙️ Ep.${episodes + 1} ${formatName} × ${subjectName} ${qualityStars}${repeatWarning}${trendNote}${adNote} → +${formatNumber(finalDownloads)} DL, +${finalSubs} subs, ${profitEmoji}${finalRevenue > 0 ? '+' : ''}${finalRevenue.toFixed(0)}€`);
    
    // Extra log messages
    if (matchedTrend) {
      addLog(`🔥 TENDANCE ! Ton sujet est viral en ce moment ! (+50% DL & subs)`);
    }
    if (adBonus > 0) {
      addLog(`📢 Publicités: +${adBonus.toFixed(0)}€ de revenus bonus (mais -${Math.floor(adPressure * 0.5)}% d'abonnés)`);
    }
  };

  // Auto-save system - DISABLED
  // useEffect(() => {
  //   const saveGame = () => {
  //     const gameState = {
  //       downloads,
  //       episodes,
  //       subscribers,
  //       money,
  //       creativity,
  //       autoProducers,
  //       studioLevel,
  //       micQuality,
  //       editingSpeed,
  //       seoLevel,
  //       socialBots,
  //       adCampaigns,
  //       numberOfAds,
  //       aiHosts,
  //       podcastNetworks,
  //       universeConversion,
  //       phase,
  //       log,
  //       unlockedFeatures
  //     };
  //     
  //     try {
  //       localStorage.setItem('universalPodcastSave', JSON.stringify(gameState));
  //     } catch (e) {
  //       console.error('Failed to save game:', e);
  //     }
  //   };

  //   // Save every 5 seconds
  //   const saveInterval = setInterval(saveGame, 5000);
  //   
  //   // Save on unmount
  //   return () => {
  //     clearInterval(saveInterval);
  //     saveGame();
  //   };
  // }, [downloads, episodes, subscribers, money, creativity, autoProducers, studioLevel, micQuality, editingSpeed, seoLevel, socialBots, adCampaigns, numberOfAds, aiHosts, podcastNetworks, universeConversion, phase, log, unlockedFeatures]);

  // Auto production tick
  // Use refs to avoid recreating the interval on every state change
  const gameStateRef = useRef({});
  
  useEffect(() => {
    gameStateRef.current = {
      autoProductionRate,
      downloadsPerEpisode,
      totalRevenuePerEpisode,
      subscriberGrowthRate,
      universeConversion,
      catalogDownloadsPerSecond,
      revenuePerDownload,
      hypeFloor,
      episodes,
      productionCost: effectiveProductionCost, // Use lobbying-adjusted cost
      maxHype,
      money,
      phase, // For trending topics
      // Phase 5/6 effects
      radioRevenuePerSecond,
      spaceDownloadsPerSecond,
      tvHypeMultiplier,
      internationalMultiplier
    };
  });

  useEffect(() => {
    const interval = setInterval(() => {
      const state = gameStateRef.current;
      
      // HYPE DECAY - 2% decay per second, but never below floor
      setCatalogHype(h => {
        const decayed = h * 0.97; // 3% decay
        return Math.max(state.hypeFloor, decayed);
      });
      
      // Catalog passive downloads (based on HYPE now)
      // Les écoutes passives génèrent des revenus mais PAS de nouveaux abonnés
      // (évite la boucle exponentielle subs → passive DL → subs)
      if (state.catalogDownloadsPerSecond > 0) {
        setDownloads(d => d + state.catalogDownloadsPerSecond);
        // Revenue = Downloads × CPM / 1000 × Fill Rate
        const catalogRevenue = state.catalogDownloadsPerSecond * state.revenuePerDownload;
        setMoney(m => m + catalogRevenue);
      }
      
      // Auto production (new episodes) - accumulator system
      if (state.autoProductionRate > 0) {
        autoEpisodeAccumRef.current += state.autoProductionRate;
        
        // Check if we've accumulated at least 1 full episode
        if (autoEpisodeAccumRef.current >= 1) {
          const episodesToProduce = Math.floor(autoEpisodeAccumRef.current);
          const autoProdCost = Math.floor(state.productionCost * 0.5) * episodesToProduce;
          
          // Check if we can afford it
          if (state.money >= autoProdCost) {
            // Produce episodes
            setEpisodes(e => e + episodesToProduce);
            
            // Hype: +20% of maxHype per episode (scales with progression)
            const hypeGain = Math.floor(state.maxHype * 0.2) * episodesToProduce;
            setCatalogHype(h => h + hypeGain);
            
            // Downloads and subs
            const newDownloads = Math.floor(episodesToProduce * state.downloadsPerEpisode);
            const newSubs = Math.floor(newDownloads * 0.10);
            setDownloads(d => d + newDownloads);
            setSubscribers(s => s + newSubs);
            
            // Revenue: gross revenue - 50% production cost
            const grossRevenue = episodesToProduce * state.downloadsPerEpisode * state.revenuePerDownload;
            const netRevenue = grossRevenue - autoProdCost;
            setMoney(m => m + netRevenue);
            
            // Keep remainder in accumulator
            autoEpisodeAccumRef.current -= episodesToProduce;
          }
          // If can't afford, accumulator keeps growing but no production happens
        }
      }
      
      // Subscriber growth (organic + bots + networks + Phase 5/6)
      if (state.subscriberGrowthRate > 0) {
        setSubscribers(s => s + state.subscriberGrowthRate);
      }
      
      // Radio passive revenue (Phase 5)
      if (state.radioRevenuePerSecond > 0) {
        setMoney(m => m + state.radioRevenuePerSecond);
      }
      
      // Space downloads (Phase 6)
      if (state.spaceDownloadsPerSecond > 0) {
        setDownloads(d => d + state.spaceDownloadsPerSecond);
        // Also adds some revenue from space downloads
        const spaceRevenue = state.spaceDownloadsPerSecond * state.revenuePerDownload;
        setMoney(m => m + spaceRevenue);
      }
      
      // Universe conversion (endgame)
      if (state.universeConversion > 0) {
        const converted = state.universeConversion * 1000000000;
        setDownloads(d => d + converted);
      }
      
      // Trending topics rotation (après 2 épisodes)
      if (state.episodes >= TREND_UNLOCK_EPISODES) {
        setTrendTimer(t => {
          if (t <= 1) {
            // Timer expired - rotate trends
            // We can't call rotateTrends directly here due to closure issues
            // So we set a flag via a separate state update
            return 0; // Signal to rotate
          }
          return t - 1;
        });
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, []); // Empty dependency array - interval never recreates!

  // ═══════════════════════════════════════════════════════════════
  // TRENDING TOPICS ROTATION
  // ═══════════════════════════════════════════════════════════════
  useEffect(() => {
    // When timer hits 0, rotate trends and reset timer (but not during ending)
    if (trendTimer === 0 && episodes >= TREND_UNLOCK_EPISODES && !isEndingActive) {
      rotateTrends();
      setTrendTimer(TREND_DURATION);
    }
  }, [trendTimer, episodes, rotateTrends, isEndingActive]);

  // Initialize trends after 2nd episode
  useEffect(() => {
    if (episodes >= TREND_UNLOCK_EPISODES && activeTrends.length === 0 && !isEndingActive) {
      rotateTrends();
    }
  }, [episodes, activeTrends.length, rotateTrends, isEndingActive]);

  // ═══════════════════════════════════════════════════════════════
  // UNIVERSE CONVERTER ENDING SEQUENCE
  // ═══════════════════════════════════════════════════════════════
  const endingIntervalRef = useRef(null);

  // Glitch state for ending sequence (separate from step to allow glitch before hide)
  const [glitchTarget, setGlitchTarget] = useState(null);
  // For fading out digits one by one (0-11, 12 = all hidden)
  const [hiddenDigits, setHiddenDigits] = useState(0);
  // For progressive panel fade-out (0 = visible, 1 = fading, 2 = hidden)
  const [fadeUpgrades, setFadeUpgrades] = useState(0);
  const [fadeStats, setFadeStats] = useState(0);
  const [fadeCreator, setFadeCreator] = useState(0);
  const [fadeTitle, setFadeTitle] = useState(0);
  const [fadeCounter, setFadeCounter] = useState(0);

  useEffect(() => {
    if (universeConversion === 1 && !isEndingActive && !endingIntervalRef.current) {
      setIsEndingActive(true);
      // Capture final stats
      finalDownloadsRef.current = downloads;
      const elapsedMs = Date.now() - gameStartTimeRef.current;
      const elapsedSec = Math.floor(elapsedMs / 1000);
      const hours = Math.floor(elapsedSec / 3600);
      const minutes = Math.floor((elapsedSec % 3600) / 60);
      const seconds = elapsedSec % 60;
      const timeString = hours > 0
        ? `${hours}h ${minutes}m ${seconds}s`
        : minutes > 0
          ? `${minutes}m ${seconds}s`
          : `${seconds}s`;

      addLog('⚫ SÉQUENCE DE CONVERSION INITIÉE...');

      // Sequence: progressive fade-out of each element
      const sequence = [
        // Upgrades panel: glitch then fade
        { delay: 1000, action: () => { setGlitchTarget('upgrades'); setLog(prev => [...prev.slice(-19), '⚫ La matière des upgrades se désagrège...']); }},
        { delay: 2500, action: () => { setFadeUpgrades(1); }}, // Start fading
        { delay: 4000, action: () => { setFadeUpgrades(2); setGlitchTarget(null); setLog(prev => [...prev.slice(-19), '⚫ Les upgrades ont été convertis en ondes sonores.']); }},

        // Stats panel: glitch then fade
        { delay: 5500, action: () => { setGlitchTarget('stats'); setLog(prev => [...prev.slice(-19), '⚫ Les statistiques perdent leur sens...']); }},
        { delay: 7000, action: () => { setFadeStats(1); }},
        { delay: 8500, action: () => { setFadeStats(2); setGlitchTarget(null); }},

        // Creator panel: glitch then fade
        { delay: 9500, action: () => { setGlitchTarget('creator'); setLog(prev => [...prev.slice(-19), '⚫ L\'interface de création s\'effrite...']); }},
        { delay: 11000, action: () => { setFadeCreator(1); }},
        { delay: 12500, action: () => { setFadeCreator(2); setGlitchTarget(null); setLog(prev => [...prev.slice(-19), '⚫ Chaque atome devient une fréquence audio.']); }},

        // Title: glitch then fade
        { delay: 14000, action: () => { setGlitchTarget('title'); setLog(prev => [...prev.slice(-19), '⚫ Le concept même de "titre" se dissout...']); }},
        { delay: 15500, action: () => { setFadeTitle(1); }},
        { delay: 17000, action: () => { setFadeTitle(2); setGlitchTarget(null); setLog(prev => [...prev.slice(-19), '⚫ Il ne reste que les chiffres. Et le journal.']); }},

        // Counter title fades
        { delay: 18500, action: () => { setFadeCounter(1); setLog(prev => [...prev.slice(-19), '⚫ Seul ce journal persiste encore...']); }},

        // Digits fade out one by one
        { delay: 20000, action: () => { setLog(prev => [...prev.slice(-19), '⚫ Les chiffres commencent à s\'effacer...']); }},
        { delay: 20500, action: () => { setHiddenDigits(1); }},
        { delay: 21300, action: () => { setHiddenDigits(2); }},
        { delay: 22100, action: () => { setHiddenDigits(3); }},
        { delay: 22900, action: () => { setHiddenDigits(4); }},
        { delay: 23700, action: () => { setHiddenDigits(5); }},
        { delay: 24500, action: () => { setHiddenDigits(6); setLog(prev => [...prev.slice(-19), '⚫ Les nombres n\'ont plus de signification...']); }},
        { delay: 25300, action: () => { setHiddenDigits(7); }},
        { delay: 26100, action: () => { setHiddenDigits(8); }},
        { delay: 26900, action: () => { setHiddenDigits(9); }},
        { delay: 27700, action: () => { setHiddenDigits(10); }},
        { delay: 28500, action: () => { setHiddenDigits(11); }},
        { delay: 29300, action: () => { setHiddenDigits(12); setLog(prev => [...prev.slice(-19), '⚫ Le dernier chiffre s\'évanouit dans le silence.']); }},

        // Hide counter completely, clear journal, grayscale
        { delay: 31000, action: () => {
          setFadeCounter(2);
          setEndingSequenceStep(5);
          // Filter out random events - keep ONLY ending sequence messages (⚫)
          setLog(prev => prev.filter(entry => entry.startsWith('⚫') || entry.trim() === '' || entry.startsWith('═')));
        }},

        // Final poetic ending like Paperclip
        { delay: 33000, action: () => { setLog(prev => [...prev.slice(-19), ' ']); }},
        { delay: 35000, action: () => { setLog(prev => [...prev.slice(-19), '⚫ L\'univers entier est devenu podcast.']); }},
        { delay: 38000, action: () => { setLog(prev => [...prev.slice(-19), '⚫ Chaque étoile, chaque planète, chaque être vivant...']); }},
        { delay: 41000, action: () => { setLog(prev => [...prev.slice(-19), '⚫ ...transformés en épisodes infinis.']); }},
        { delay: 44000, action: () => { setLog(prev => [...prev.slice(-19), ' ']); }},
        { delay: 46000, action: () => { setLog(prev => [...prev.slice(-19), '⚫ Il n\'y a plus d\'auditeurs.']); }},
        { delay: 49000, action: () => { setLog(prev => [...prev.slice(-19), '⚫ Car tout est devenu le podcast.']); }},
        { delay: 52000, action: () => { setLog(prev => [...prev.slice(-19), ' ']); }},
        { delay: 54000, action: () => { setLog(prev => [...prev.slice(-19), '⚫ Et le podcast joue.']); }},
        { delay: 57000, action: () => { setLog(prev => [...prev.slice(-19), '⚫ Pour personne.']); }},
        { delay: 60000, action: () => { setLog(prev => [...prev.slice(-19), '⚫ Pour toujours.']); }},
        { delay: 64000, action: () => { setLog(prev => [...prev.slice(-19), ' ']); }},
        { delay: 67000, action: () => { setLog(prev => [...prev.slice(-19), '════════════════════════════════']); }},
        { delay: 68000, action: () => { setLog(prev => [...prev.slice(-19), ' ']); }},
        { delay: 69000, action: () => { setLog(prev => [...prev.slice(-19), `⚫ ÉCOUTES TOTALES : ${finalDownloadsRef.current.toLocaleString('fr-FR')}`]); }},
        { delay: 71000, action: () => { setLog(prev => [...prev.slice(-19), `⚫ TEMPS DE JEU : ${timeString}`]); }},
        { delay: 73000, action: () => { setLog(prev => [...prev.slice(-19), ' ']); }},
        { delay: 75000, action: () => { setLog(prev => [...prev.slice(-19), '════════════════════════════════']); }},
        { delay: 77000, action: () => { setLog(prev => [...prev.slice(-19), ' ']); }},
        { delay: 79000, action: () => { setLog(prev => [...prev.slice(-19), '⚫ FIN']); }},
      ];

      sequence.forEach(({ delay, action }) => {
        setTimeout(action, delay);
      });

      // Store a dummy value to prevent re-triggering
      endingIntervalRef.current = true;
    }
  }, [universeConversion, isEndingActive, addLog]);

  // ═══════════════════════════════════════════════════════════════
  // SPONSOR SYSTEM (Phase 3+)
  // ═══════════════════════════════════════════════════════════════
  
  // Fonctions sponsor supprimées - remplacées par la pression publicitaire

  // ═══════════════════════════════════════════════════════════════
  // MILESTONE CHECK - Progressive unlocks system
  // ═══════════════════════════════════════════════════════════════
  // Milestone check - single pass with batched updates
  const lastMilestoneCheck = useRef({ downloads: -1, subscribers: -1 });
  
  useEffect(() => {
    // Skip if values haven't changed
    if (downloads === lastMilestoneCheck.current.downloads && 
        subscribers === lastMilestoneCheck.current.subscribers) {
      return;
    }
    lastMilestoneCheck.current = { downloads, subscribers };
    
    // Check all milestones and collect what needs to be unlocked
    setUnlockedMilestones(currentUnlocked => {
      const toUnlock = [];
      const messages = [];
      let phaseChange = null;
      
      MILESTONES.forEach(milestone => {
        if (currentUnlocked.includes(milestone.id)) return;
        if (toUnlock.includes(milestone.id)) return;
        
        const currentValue = milestone.type === 'downloads' ? downloads : subscribers;
        
        if (currentValue >= milestone.threshold) {
          toUnlock.push(milestone.id);
          messages.push(milestone.message);
          if (milestone.phase) phaseChange = milestone.phase;
        }
      });
      
      // Apply side effects via setTimeout to avoid nested setState issues
      if (toUnlock.length > 0) {
        setTimeout(() => {
          messages.forEach(msg => addLog(msg));
          if (phaseChange) setPhase(phaseChange);
        }, 0);
        
        return [...currentUnlocked, ...toUnlock];
      }
      
      return currentUnlocked;
    });
  }, [downloads, subscribers, addLog]);

  // Upgrades - Progressive unlock system
  const upgrades = {
    // ═══ PRODUCTION ═══
    micUpgrade: {
      name: micLevel === 0 ? '🎙️ Micro XLR' : micLevel === 1 ? '🎙️ Micro Studio' : '🎤 Max niveau',
      cost: micLevel < 2 ? MIC_UPGRADES[micLevel + 1]?.cost : 0,
      effect: () => { 
        if (micLevel < 2) {
          const cost = MIC_UPGRADES[micLevel + 1].cost;
          setMicLevel(m => m + 1); 
          setMoney(m => m - cost); 
          addLog(`🎤 Upgrade: ${MIC_UPGRADES[micLevel + 1].name} !`); 
        }
      },
      available: micLevel < 2 && phase >= 1,
      description: micLevel < 2 ? `${MIC_UPGRADES[micLevel + 1]?.desc} (x${MIC_UPGRADES[micLevel + 1]?.bonus} downloads)` : 'Niveau maximum atteint',
      category: 'production',
      currentLevel: `Actuel: ${MIC_UPGRADES[micLevel]?.name}`
    },
    editingUpgrade: {
      name: editingLevel === 0 ? '✂️ Éditeur Freelance' : editingLevel === 1 ? '✂️ Agence Audio' : '✂️ Max niveau',
      cost: editingLevel < 2 ? EDITING_UPGRADES[editingLevel + 1]?.cost : 0,
      effect: () => { 
        if (editingLevel < 2) {
          const cost = EDITING_UPGRADES[editingLevel + 1].cost;
          setEditingLevel(e => e + 1); 
          setMoney(m => m - cost); 
          addLog(`✂️ Upgrade: ${EDITING_UPGRADES[editingLevel + 1].name} !`); 
        }
      },
      available: editingLevel < 2 && phase >= 1,
      description: editingLevel < 2 ? `${EDITING_UPGRADES[editingLevel + 1]?.desc} (x${EDITING_UPGRADES[editingLevel + 1]?.bonus} downloads)` : 'Niveau maximum atteint',
      category: 'production',
      currentLevel: `Actuel: ${EDITING_UPGRADES[editingLevel]?.name}`
    },

    // ═══ MARKETING (Phase 2+) ═══
    seo: {
      name: `📈 PSO Lvl ${seoLevel + 1}`,
      cost: 300 * Math.pow(2, seoLevel),
      effect: () => { setSeoLevel(s => s + 1); setMoney(m => m - 300 * Math.pow(2, seoLevel)); addLog('📈 PSO amélioré ! +50% conversion abonnés'); },
      available: phase >= 2,
      description: '+50% conversion en abonnés',
      category: 'marketing'
    },
    socialBots: {
      name: `🤖 Bots Sociaux ${socialBots > 0 ? `(${socialBots})` : ''}`,
      cost: Math.floor(500 * Math.pow(1.8, socialBots)),
      effect: () => { setSocialBots(s => s + 1); setMoney(m => m - Math.floor(500 * Math.pow(1.8, socialBots))); addLog(`🤖 Bots déployés ! +${Math.pow(1.8, socialBots).toFixed(1)} subs/sec`); },
      available: phase >= 2,
      description: `+${Math.pow(1.8, socialBots).toFixed(1)} abonnés/sec (scaling)`,
      category: 'marketing'
    },
    
    // ═══ EXPANSION (Phase 4+) ═══
    aiHost: {
      name: `🤖 Hôte IA ${aiHosts > 0 ? `(${aiHosts})` : ''}`,
      cost: Math.floor(100000 * Math.pow(2, aiHosts)),
      effect: () => { setAiHosts(a => a + 1); setMoney(m => m - Math.floor(100000 * Math.pow(2, aiHosts))); addLog('🤖 Hôte IA créé ! Il produit sans relâche.'); },
      available: phase >= 4,
      description: '+0.1 épisode/sec automatique (toutes les 10s)',
      category: 'advanced'
    },
    network: {
      name: `🌐 Réseau Podcasts ${podcastNetworks > 0 ? `(${podcastNetworks})` : ''}`,
      cost: Math.floor(50000 * Math.pow(3, podcastNetworks)),
      effect: () => { setPodcastNetworks(p => p + 1); setMoney(m => m - Math.floor(50000 * Math.pow(3, podcastNetworks))); addLog('🌐 Nouveau réseau acquis !'); },
      available: phase >= 4,
      description: '+10 abonnés/sec automatique',
      category: 'advanced'
    },

    // ═══ DOMINATION (Phase 5+) ═══
    international: {
      name: `🌍 Expansion Internationale ${internationalLevel > 0 ? `(${internationalLevel})` : ''}`,
      cost: Math.floor(500000 * Math.pow(2.5, internationalLevel)),
      effect: () => { 
        setInternationalLevel(i => i + 1); 
        setMoney(m => m - Math.floor(500000 * Math.pow(2.5, internationalLevel))); 
        addLog('🌍 Expansion internationale ! Audience ×2 !'); 
      },
      available: phase >= 5,
      description: '×2 audience totale (multiplicateur)',
      category: 'domination'
    },
    radio: {
      name: `📡 Chaîne Radio ${radioLevel > 0 ? `(${radioLevel})` : ''}`,
      cost: Math.floor(1000000 * Math.pow(3, radioLevel)),
      effect: () => { 
        setRadioLevel(r => r + 1); 
        setMoney(m => m - Math.floor(1000000 * Math.pow(3, radioLevel))); 
        addLog('📡 Chaîne radio lancée ! Revenus passifs décuplés !'); 
      },
      available: phase >= 5,
      description: '+500€/sec revenus passifs',
      category: 'domination'
    },
    tv: {
      name: `🎬 Adaptation TV ${tvLevel > 0 ? `(${tvLevel})` : ''}`,
      cost: Math.floor(5000000 * Math.pow(4, tvLevel)),
      effect: () => { 
        setTvLevel(t => t + 1); 
        setMoney(m => m - Math.floor(5000000 * Math.pow(4, tvLevel))); 
        addLog('🎬 Adaptation TV ! Ta fame explose !'); 
      },
      available: phase >= 5,
      description: '+100 abonnés/sec, ×1.5 hype',
      category: 'domination'
    },
    lobbying: {
      name: `🏛️ Lobbying Culturel ${lobbyingLevel > 0 ? `(${lobbyingLevel})` : ''}`,
      cost: Math.floor(10000000 * Math.pow(5, lobbyingLevel)),
      effect: () => { 
        setLobbyingLevel(l => l + 1); 
        setMoney(m => m - Math.floor(10000000 * Math.pow(5, lobbyingLevel))); 
        addLog('🏛️ Lobbying réussi ! Coûts réduits !'); 
      },
      available: phase >= 5,
      description: '-25% coûts production',
      category: 'domination'
    },
    
    // ═══ SINGULARITÉ (Phase 6+) ═══
    mindControl: {
      name: `🧠 Contrôle Mental ${mindControlLevel > 0 ? `(${mindControlLevel})` : ''}`,
      cost: Math.floor(100000000 * Math.pow(3, mindControlLevel)),
      effect: () => { 
        setMindControlLevel(m => m + 1); 
        setMoney(m => m - Math.floor(100000000 * Math.pow(3, mindControlLevel))); 
        addLog('🧠 Contrôle mental activé ! Les auditeurs ne peuvent plus s\'arrêter...'); 
      },
      available: phase >= 6,
      description: '+1000 abonnés/sec, zéro churn',
      category: 'singularity'
    },
    space: {
      name: `🛸 Diffusion Spatiale ${spaceLevel > 0 ? `(${spaceLevel})` : ''}`,
      cost: Math.floor(1000000000 * Math.pow(5, spaceLevel)),
      effect: () => { 
        setSpaceLevel(s => s + 1); 
        setMoney(m => m - Math.floor(1000000000 * Math.pow(5, spaceLevel))); 
        addLog('🛸 Diffusion spatiale ! Les aliens écoutent !'); 
      },
      available: phase >= 6,
      description: '+1M downloads/sec',
      category: 'singularity'
    },
    universe: {
      name: `🌌 Convertisseur Universel ${universeConversion > 0 ? '(ACTIVÉ)' : ''}`,
      cost: 1000000000000,
      effect: () => {
        setUniverseConversion(1);
        setMoney(m => m - 1000000000000);
        addLog('🌌 LA MATIÈRE SE TRANSFORME EN ONDES PODCAST...');
        addLog('⚫ Universe Converter activé ! La fin approche...');
      },
      available: phase >= 6 && universeConversion === 0,
      description: 'Convertit l\'univers en podcast. Point de non-retour.',
      category: 'singularity'
    }
  };

  const upgradeCategories = {
    production: { name: '🎚️ PRODUCTION', color: '#00ff88' },
    marketing: { name: '📣 MARKETING', color: '#ffcc00' },
    advanced: { name: '🚀 EXPANSION', color: '#00ccff' },
    domination: { name: '🇫🇷 DOMINATION', color: '#ff9900' },
    singularity: { name: '🧠 SINGULARITÉ', color: '#ff6b6b' }
  };

  // Debug: Generate quick episode with specified score
  const debugGenerateEpisode = () => {
    const score = debugScore;
    const format = unlockedFormats[0] || 'interview';
    const subject = unlockedSubjects[0] || 'tech';
    
    // Calculate boost multipliers from debugBoosts
    const debugBoostDlMult = 1 + Object.entries(debugBoosts)
      .filter(([_, active]) => active)
      .reduce((sum, [key]) => sum + (BOOSTS[key]?.dlBonus || 0), 0);
    const debugBoostSubMult = 1 + Object.entries(debugBoosts)
      .filter(([_, active]) => active)
      .reduce((sum, [key]) => sum + (BOOSTS[key]?.subBonus || 0), 0);
    const debugBoostCost = Object.entries(debugBoosts)
      .filter(([_, active]) => active)
      .reduce((sum, [key]) => sum + getBoostCost(key), 0);
    
    const totalCost = productionCost + debugBoostCost;
    
    // Check if player can afford it
    if (money < totalCost) {
      addLog(`🔧 DEBUG: Pas assez d'argent (${Math.floor(money)}€ / ${totalCost}€ requis)`);
      return;
    }
    
    // Use REAL game formulas with boosts
    const debugScoreMultiplier = score / 5;
    const finalDownloads = Math.floor(baseDownloadsPerEpisode * debugScoreMultiplier * debugBoostDlMult);
    const subConversion = 0.10 * debugScoreMultiplier * debugBoostSubMult;
    const finalSubs = Math.floor(finalDownloads * subConversion);
    const grossRevenue = Math.floor(finalDownloads * revenuePerDownload);
    const netRevenue = grossRevenue - totalCost;

    // Apply results
    setMoney(m => m - totalCost + grossRevenue);
    setEpisodes(e => e + 1);
    setCatalogHype(Math.floor(500 * Math.log10(episodes + 2)));
    setDownloads(d => d + finalDownloads);
    setSubscribers(s => s + finalSubs);
    setLastCombo(`${format}-${subject}`);
    
    const boostNames = Object.entries(debugBoosts).filter(([_,v]) => v).map(([k]) => BOOSTS[k]?.name).join(', ');
    addLog(`🔧 DEBUG Ep.${episodes + 1} (${score.toFixed(1)}/10)${boostNames ? ` [${boostNames}]` : ''} → +${formatNumber(finalDownloads)} DL, +${finalSubs} subs, ${netRevenue >= 0 ? '+' : ''}${netRevenue}€`);
  };

  // Keyboard shortcuts for dev panels
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+Shift+D = Debug panel
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        setShowDebugPanel(p => !p);
      }
      // Ctrl+Shift+V = Dev panel
      if (e.ctrlKey && e.shiftKey && e.key === 'V') {
        e.preventDefault();
        setShowDevPanel(p => !p);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div
      className={isEndingActive && endingSequenceStep < 5 ? 'glitch-intense' : ''}
      style={{
        minHeight: '100vh',
        backgroundColor: endingSequenceStep >= 5 ? '#000000' : '#0a0a0a',
        color: endingSequenceStep >= 5 ? '#ffffff' : '#00ff88',
        fontFamily: '"IBM Plex Mono", "Courier New", monospace',
        padding: '20px',
        fontSize: '14px',
        lineHeight: '1.6',
        filter: endingSequenceStep >= 5 ? 'grayscale(100%)' : 'none',
        transition: 'background-color 2s, color 2s, filter 2s'
      }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;600;700&display=swap');

        * { box-sizing: border-box; }

        .glow {
          text-shadow: 0 0 10px #00ff88, 0 0 20px #00ff88, 0 0 30px #00ff88;
        }

        /* GLITCH EFFECTS - Softer version */
        .glitch-intense {
          animation: glitchIntense 0.3s infinite, colorGlitch 0.5s infinite, flicker 0.2s infinite;
        }

        @keyframes glitchIntense {
          0%, 100% { transform: translate(0) skew(0deg); }
          20% { transform: translate(-3px, 2px) skew(0.5deg); }
          40% { transform: translate(2px, -3px) skew(-0.5deg); }
          60% { transform: translate(-2px, -2px) skew(0.3deg); }
          80% { transform: translate(3px, 2px) skew(-0.3deg); }
        }

        @keyframes colorGlitch {
          0%, 100% { filter: hue-rotate(0deg) brightness(1); }
          25% { filter: hue-rotate(10deg) brightness(1.1); }
          50% { filter: hue-rotate(-10deg) brightness(0.9); }
          75% { filter: hue-rotate(5deg) brightness(1.05); }
        }

        @keyframes flicker {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.85; }
        }

        .fade-out {
          opacity: 0.5;
          transition: opacity 1.5s ease-out;
        }

        .fade-hidden {
          opacity: 0;
          pointer-events: none;
          transition: opacity 1.5s ease-out;
        }
        
        .odometer-digit {
          transition: all 0.15s ease-out;
        }
        
        .odometer-digit:hover {
          transform: scale(1.1);
          box-shadow: 0 0 15px #00ff8888;
        }
        
        .panel {
          background: rgba(0, 255, 136, 0.05);
          border: 1px solid #00ff8844;
          padding: 15px;
          margin-bottom: 15px;
        }
        
        .btn {
          background: transparent;
          border: 1px solid #00ff88;
          color: #00ff88;
          padding: 8px 16px;
          cursor: pointer;
          font-family: inherit;
          font-size: 13px;
          transition: all 0.2s;
          margin: 5px 5px 5px 0;
        }
        
        .btn:hover:not(:disabled) {
          background: #00ff88;
          color: #0a0a0a;
          box-shadow: 0 0 15px #00ff8888;
        }
        
        .btn:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }
        
        .btn-record {
          border-color: #ff6b6b;
          color: #ff6b6b;
          font-size: 16px;
          padding: 15px 30px;
        }
        
        .btn-record:hover:not(:disabled) {
          background: #ff6b6b;
          color: #0a0a0a;
          box-shadow: 0 0 20px #ff6b6b88;
        }
        
        .progress-bar {
          height: 8px;
          background: #1a1a1a;
          border: 1px solid #00ff8844;
          margin-top: 5px;
        }
        
        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #00ff88, #00ffcc);
          transition: width 0.3s;
        }
        
        .log-container {
          height: 200px;
          overflow-y: auto;
          background: #050505;
          border: 1px solid #00ff8822;
          padding: 10px;
          font-size: 12px;
        }
        
        .log-entry {
          padding: 2px 0;
          border-bottom: 1px solid #00ff8811;
        }
        
        .stat-row {
          display: flex;
          justify-content: space-between;
          padding: 5px 0;
          border-bottom: 1px dashed #00ff8822;
        }
        
        .upgrade-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 10px;
        }
        
        .upgrade-card {
          background: rgba(0, 255, 136, 0.03);
          border: 1px solid #00ff8833;
          padding: 12px;
        }
        
        .upgrade-card:hover {
          border-color: #00ff88;
        }
        
        .phase-indicator {
          position: fixed;
          top: 20px;
          right: 20px;
          padding: 10px 20px;
          border: 2px solid;
          border-radius: 8px;
          font-weight: 700;
          font-size: 14px;
          background: rgba(0, 0, 0, 0.85);
          backdrop-filter: blur(10px);
          z-index: 1000;
          animation: phasePulse 2s infinite;
        }

        @keyframes phasePulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }

        .phase-1 { border-color: #00ff88; color: #00ff88; }
        .phase-2 { border-color: #ffcc00; color: #ffcc00; }
        .phase-3 { border-color: #ff6b6b; color: #ff6b6b; }
        .phase-4 { border-color: #00ccff; color: #00ccff; text-shadow: 0 0 15px #00ccff; }
        .phase-5 { border-color: #ff9900; color: #ff9900; text-shadow: 0 0 15px #ff9900; }
        .phase-6 { border-color: #ff6b6b; color: #ff6b6b; text-shadow: 0 0 20px #ff6b6b; animation: phasePulse 1.5s ease-in-out infinite; }
      `}</style>

      {/* DEV Panel (Ctrl+Shift+V) */}
      {showDevPanel && (
        <div style={{
          position: 'fixed',
          top: '10px',
          right: '10px',
          background: '#1a1a2eee',
          border: '2px solid #ff6b6b',
          borderRadius: '8px',
          padding: '15px',
          zIndex: 1000,
          width: '280px',
          maxHeight: '80vh',
          overflowY: 'auto'
        }}>
          <div style={{ color: '#ff6b6b', fontWeight: 'bold', marginBottom: '15px', fontSize: '14px' }}>
            🛠️ MODE DÉVELOPPEUR
          </div>
          
          {/* Phase Selector */}
          <div style={{ marginBottom: '15px' }}>
            <div style={{ color: '#888', fontSize: '11px', marginBottom: '5px' }}>PHASE</div>
            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
              {[1, 2, 3, 4, 5, 6].map(p => (
                <button
                  key={p}
                  onClick={() => setPhase(p)}
                  style={{
                    background: phase === p ? '#00ff88' : '#333',
                    color: phase === p ? '#000' : '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '5px 12px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
          
          {/* Money */}
          <div style={{ marginBottom: '15px' }}>
            <div style={{ color: '#888', fontSize: '11px', marginBottom: '5px' }}>💰 ARGENT</div>
            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
              {[
                { label: '+10K', value: 10000 },
                { label: '+100K', value: 100000 },
                { label: '+1M', value: 1000000 },
                { label: '+10M', value: 10000000 }
              ].map(({ label, value }) => (
                <button
                  key={label}
                  onClick={() => setMoney(m => m + value)}
                  style={{
                    background: '#ffcc0033',
                    color: '#ffcc00',
                    border: '1px solid #ffcc0066',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    cursor: 'pointer',
                    fontSize: '11px'
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Downloads */}
          <div style={{ marginBottom: '15px' }}>
            <div style={{ color: '#888', fontSize: '11px', marginBottom: '5px' }}>📥 DOWNLOADS</div>
            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
              {[
                { label: '+100K', value: 100000 },
                { label: '+1M', value: 1000000 },
                { label: '+10M', value: 10000000 },
                { label: '+100M', value: 100000000 }
              ].map(({ label, value }) => (
                <button
                  key={label}
                  onClick={() => setDownloads(d => d + value)}
                  style={{
                    background: '#00ff8833',
                    color: '#00ff88',
                    border: '1px solid #00ff8866',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    cursor: 'pointer',
                    fontSize: '11px'
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Subscribers */}
          <div style={{ marginBottom: '15px' }}>
            <div style={{ color: '#888', fontSize: '11px', marginBottom: '5px' }}>👥 ABONNÉS</div>
            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
              {[
                { label: '+1K', value: 1000 },
                { label: '+10K', value: 10000 },
                { label: '+100K', value: 100000 },
                { label: '+1M', value: 1000000 }
              ].map(({ label, value }) => (
                <button
                  key={label}
                  onClick={() => setSubscribers(s => s + value)}
                  style={{
                    background: '#00ccff33',
                    color: '#00ccff',
                    border: '1px solid #00ccff66',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    cursor: 'pointer',
                    fontSize: '11px'
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Episodes */}
          <div style={{ marginBottom: '15px' }}>
            <div style={{ color: '#888', fontSize: '11px', marginBottom: '5px' }}>🎙️ ÉPISODES</div>
            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
              {[
                { label: '+5', value: 5 },
                { label: '+20', value: 20 },
                { label: '+50', value: 50 },
                { label: '+100', value: 100 }
              ].map(({ label, value }) => (
                <button
                  key={label}
                  onClick={() => setEpisodes(e => e + value)}
                  style={{
                    background: '#cc00ff33',
                    color: '#cc00ff',
                    border: '1px solid #cc00ff66',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    cursor: 'pointer',
                    fontSize: '11px'
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Ad Pressure */}
          <div style={{ marginBottom: '15px' }}>
            <div style={{ color: '#888', fontSize: '11px', marginBottom: '5px' }}>📺 PRESSION PUB</div>
            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
              <button
                onClick={() => setAdPressure(0)}
                style={{
                  background: '#00ff8833',
                  color: '#00ff88',
                  border: '1px solid #00ff8866',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  cursor: 'pointer',
                  fontSize: '11px'
                }}
              >
                0%
              </button>
              <button
                onClick={() => setAdPressure(50)}
                style={{
                  background: '#ffcc0033',
                  color: '#ffcc00',
                  border: '1px solid #ffcc0066',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  cursor: 'pointer',
                  fontSize: '11px'
                }}
              >
                50%
              </button>
              <button
                onClick={() => setAdPressure(100)}
                style={{
                  background: '#ff6b6b33',
                  color: '#ff6b6b',
                  border: '1px solid #ff6b6b66',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  cursor: 'pointer',
                  fontSize: '11px'
                }}
              >
                100%
              </button>
            </div>
          </div>
          
          {/* Pression Publicitaire */}
          <div style={{ marginBottom: '15px' }}>
            <div style={{ color: '#888', fontSize: '11px', marginBottom: '5px' }}>📢 PRESSION PUB</div>
            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
              {[0, 25, 50, 75, 100].map(value => (
                <button
                  key={value}
                  onClick={() => setAdPressure(value)}
                  style={{
                    background: value === 0 ? '#00ff8833' : value <= 50 ? '#ffcc0033' : '#ff6b6b33',
                    color: value === 0 ? '#00ff88' : value <= 50 ? '#ffcc00' : '#ff6b6b',
                    border: `1px solid ${value === 0 ? '#00ff88' : value <= 50 ? '#ffcc00' : '#ff6b6b'}66`,
                    borderRadius: '4px',
                    padding: '4px 8px',
                    cursor: 'pointer',
                    fontSize: '10px'
                  }}
                >
                  {value}%
                </button>
              ))}
            </div>
          </div>
          
          {/* Quick Setup Buttons */}
          <div style={{ borderTop: '1px solid #333', paddingTop: '10px' }}>
            <div style={{ color: '#888', fontSize: '11px', marginBottom: '5px' }}>⚡ SETUP RAPIDE</div>
            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
              <button
                onClick={() => {
                  setPhase(3);
                  setMoney(50000);
                  setDownloads(500000);
                  setSubscribers(5000);
                  setEpisodes(30);
                }}
                style={{
                  background: '#6666ff33',
                  color: '#6666ff',
                  border: '1px solid #6666ff66',
                  borderRadius: '4px',
                  padding: '6px 10px',
                  cursor: 'pointer',
                  fontSize: '11px'
                }}
              >
                🎯 Phase 3
              </button>
              <button
                onClick={() => {
                  setPhase(5);
                  setMoney(5000000);
                  setDownloads(50000000);
                  setSubscribers(500000);
                  setEpisodes(200);
                }}
                style={{
                  background: '#ff00ff33',
                  color: '#ff00ff',
                  border: '1px solid #ff00ff66',
                  borderRadius: '4px',
                  padding: '6px 10px',
                  cursor: 'pointer',
                  fontSize: '11px'
                }}
              >
                🚀 Phase 5
              </button>
              <button
                onClick={() => {
                  setPhase(6);
                  setMoney(100000000);
                  setDownloads(1000000000);
                  setSubscribers(10000000);
                  setEpisodes(500);
                }}
                style={{
                  background: '#ff660033',
                  color: '#ff6600',
                  border: '1px solid #ff660066',
                  borderRadius: '4px',
                  padding: '6px 10px',
                  cursor: 'pointer',
                  fontSize: '11px'
                }}
              >
                🌌 Phase 6
              </button>
            </div>
          </div>
        </div>
      )}

      {fadeTitle < 2 && (
        <div className={`phase-indicator phase-${phase} ${glitchTarget === 'title' ? 'glitch-intense' : ''} ${fadeTitle === 1 ? 'fade-out' : ''}`}>
          PHASE {phase}: {phaseNames[phase]}
        </div>
      )}

      {fadeTitle < 2 && (
        <h1 className={`glow ${glitchTarget === 'title' ? 'glitch-intense' : ''} ${fadeTitle === 1 ? 'fade-out' : ''}`} style={{
          fontSize: '32px',
          marginBottom: '30px',
          letterSpacing: '3px',
          transition: 'opacity 1.5s'
        }}>
          🎙️ PODCAST SIMULATOR
        </h1>
      )}

      {/* Big Downloads Counter - stays visible until fadeCounter === 2 */}
      {fadeCounter < 2 && (
      <div className={`panel ${glitchTarget === 'counter' ? 'glitch-intense' : ''} ${fadeCounter === 1 ? 'fade-out' : ''}`} style={{ marginBottom: '20px', textAlign: 'center' }}>
        {endingSequenceStep < 4 && (
          <div style={{ color: '#00ff8888', fontSize: '14px', marginBottom: '10px' }}>
            📥 DOWNLOADS TOTAUX
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '4px', marginBottom: '15px' }}>
          {(() => {
            const numStr = String(Math.floor(downloads)).padStart(12, '0');
            const firstNonZeroIndex = numStr.split('').findIndex(d => d !== '0');
            const startGlowIndex = firstNonZeroIndex === -1 ? 11 : firstNonZeroIndex;

            return numStr.split('').map((digit, i) => {
              const shouldGlow = i >= startGlowIndex;
              // Hide digits from right to left (index 11, 10, 9, ... 0)
              const digitIndexFromRight = 11 - i;
              const isHidden = digitIndexFromRight < hiddenDigits;

              return (
                <div
                  key={i}
                  className="odometer-digit"
                  style={{
                    width: '36px',
                    height: '54px',
                    background: isHidden ? 'transparent' : 'linear-gradient(180deg, #1a1a1a 0%, #0d0d0d 50%, #1a1a1a 100%)',
                    border: isHidden ? '1px solid transparent' : `1px solid ${shouldGlow ? '#00ff88' : '#00ff8833'}`,
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '32px',
                    fontWeight: '700',
                    color: shouldGlow ? '#00ff88' : '#00ff8833',
                    textShadow: shouldGlow && !isHidden ? '0 0 15px #00ff88, 0 0 30px #00ff8844' : 'none',
                    fontFamily: '"IBM Plex Mono", monospace',
                    boxShadow: shouldGlow && !isHidden ? '0 0 10px #00ff8822 inset' : 'none',
                    opacity: isHidden ? 0 : 1,
                    transition: 'opacity 0.8s ease-out, background 0.8s, border 0.8s'
                  }}>
                  {isHidden ? '' : digit}
                </div>
              );
            });
          })()}
        </div>
      </div>
      )}

      {endingSequenceStep >= 4 ? (
        // Final ending: only show the journal
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          background: endingSequenceStep >= 5 ? 'transparent' : 'rgba(10, 10, 10, 0.9)',
          border: endingSequenceStep >= 5 ? 'none' : '1px solid #ff000044',
          borderRadius: '8px',
          padding: endingSequenceStep >= 5 ? '0' : '20px',
          transition: 'all 2s ease'
        }}>
          {endingSequenceStep < 5 && (
            <h2 style={{ marginTop: 0, borderBottom: '1px solid #ff000044', paddingBottom: '10px', textAlign: 'center', color: '#ff0000' }}>
              ⚫ JOURNAL UNIVERSEL ⚫
            </h2>
          )}
          <div style={{
            maxHeight: '70vh',
            overflowY: 'auto',
            padding: endingSequenceStep >= 5 ? '20px' : '10px',
          }}>
            {log.slice(-50).map((entry, i) => (
              <div
                key={i}
                style={{
                  color: endingSequenceStep >= 5 ? '#888888' : (entry.includes('⚫') ? '#ff0000' : '#00ff88'),
                  marginBottom: '8px',
                  fontSize: endingSequenceStep >= 5 ? '13px' : '14px',
                  fontFamily: 'monospace'
                }}
              >
                {entry}
              </div>
            ))}
          </div>
        </div>
      ) : (
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Colonne gauche: Stats + Button + Pub */}
          {fadeStats < 2 && (
          <div className={`${glitchTarget === 'stats' ? 'glitch-intense' : ''} ${fadeStats === 1 ? 'fade-out' : ''}`} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>

        {/* Stats Panel */}
        <div className="panel">
          <h2 style={{ marginTop: 0, borderBottom: '1px solid #00ff8844', paddingBottom: '10px' }}>
            📊 STATISTIQUES
          </h2>
          <div className="stat-row">
            <span>Épisodes produits</span>
            <span>{formatNumber(episodes)}</span>
          </div>
          <div className="stat-row">
            <span>Abonnés</span>
            <span className="glow">{formatNumber(subscribers)}</span>
          </div>
          <div className="stat-row">
            <span>📥 Downloads totaux</span>
            <span style={{ color: '#00ccff', fontFamily: 'monospace' }}>{formatNumberFull(downloads)}</span>
          </div>
          <div className="stat-row">
            <span>💰 Argent</span>
            <span style={{ color: '#ffcc00' }}>{formatNumber(money)} €</span>
          </div>
          <div className="stat-row">
            <span>📀 CPM</span>
            <span style={{ color: '#ffcc00' }}>{cpm}€ / 1000 écoutes</span>
          </div>
          <div className="stat-row">
            <span>📊 Fill Rate</span>
            <span style={{ color: fillRate >= 0.5 ? '#00ff88' : '#ff9900' }}>
              {Math.round(fillRate * 100)}% 
              <span style={{ fontSize: '10px', color: '#666', marginLeft: '5px' }}>
                ({subscribers >= 50000 ? '50k+ subs' : subscribers >= 10000 ? '10k+ subs' : subscribers >= 2000 ? '2k+ subs' : subscribers >= 500 ? '500+ subs' : '< 500 subs'})
              </span>
            </span>
          </div>
          <div className="stat-row">
            <span>💵 Revenus passifs</span>
            <span style={{ color: revenuePerSecond > 0 ? '#00ff88' : '#666' }}>
              {revenuePerSecond > 0 ? `+${revenuePerSecond.toFixed(2)}€/sec` : '0€/sec'}
            </span>
          </div>
          
          {/* Hype Catalogue Bar - appears after 2nd episode */}
          {episodes >= 2 && (
            <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #ff660044' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>🔥 Hype Catalogue</span>
                <span style={{ 
                  color: hypePercentage >= 50 ? '#ff6600' : hypePercentage >= 20 ? '#ffcc00' : '#ff6b6b',
                  fontWeight: '600'
                }}>
                  {Math.floor(effectiveHype)} pts
                </span>
              </div>
              <div className="progress-bar">
                <div className="progress-fill" style={{ 
                  width: `${Math.min(100, hypePercentage)}%`,
                  background: hypePercentage >= 70 
                    ? 'linear-gradient(90deg, #ff6600, #ff00ff)' 
                    : hypePercentage >= 40 
                      ? 'linear-gradient(90deg, #ffcc00, #ff6600)' 
                      : 'linear-gradient(90deg, #ff6b6b, #ff9900)'
                }} />
              </div>
              <div style={{ 
                fontSize: '11px', 
                color: hypePercentage >= 50 ? '#ff660088' : '#ff6b6b88', 
                marginTop: '3px'
              }}>
                {hypePercentage >= 70 ? '🔥 EN FEU ! Ton podcast est viral !' : 
                 hypePercentage >= 40 ? '📈 Bonne dynamique, continue !' : 
                 hypePercentage >= 20 ? '😐 La hype retombe... publie un épisode !' :
                 '❄️ Ton catalogue est froid. Publie pour relancer !'}
              </div>
              <div style={{ fontSize: '10px', color: '#666', marginTop: '5px' }}>
                Plancher SEO: {hypeFloor} pts · Max: {maxHype} pts
              </div>
            </div>
          )}

          {/* Catalog Passive - Your Inventory */}
          {episodes > 0 && (
            <div style={{ 
              marginTop: '15px', 
              paddingTop: '15px', 
              borderTop: '1px solid #00ccff44'
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <span style={{ color: '#00ccff', fontWeight: '600' }}>📚 Catalogue</span>
                <span style={{ color: '#00ccff', fontWeight: '700', fontSize: '16px' }}>
                  {episodes} épisodes
                </span>
              </div>
              <div style={{ fontSize: '11px', color: '#00ccff88', lineHeight: '1.6' }}>
                <div>└ Écoutes passives: +{formatNumber(catalogDownloadsPerSecond)}/sec</div>
                <div>└ (Hype {Math.floor(effectiveHype)} × {formatNumber(subscribers)} subs × 0.1%)</div>
                <div style={{ color: '#ff660088', marginTop: '4px' }}>
                  ⚠️ La Hype décroît de 3%/sec. Publie régulièrement !
                </div>
              </div>
            </div>
          )}
          
          {/* Message when catalog inactive */}
          {episodes > 0 && subscribers === 0 && (
            <div style={{ 
              marginTop: '15px', 
              paddingTop: '15px', 
              borderTop: '1px solid #00ccff44'
            }}>
              <div style={{ fontSize: '11px', color: '#00ccff66', fontStyle: 'italic' }}>
                📚 Catalogue inactif : Besoin d'abonnés pour générer des écoutes passives
              </div>
            </div>
          )}
        </div>

        {/* Create Episode Button */}
        <div className="panel" style={{ textAlign: 'center', padding: '30px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <button
            className="btn btn-record"
            onClick={() => {
              setShowCreator(true);
              setCreatorStep(1);
              setEpisodeResults(null); // Clear previous results
              // Reset episode choices
              setSliderPrep(33);
              setSliderPerf(34);
              setSliderProd(33);
              setEpisodeBoosts({
                socialAds: false,
                guestStar: false,
                proCover: false,
                crossPromo: false,
                newsletter: false
              });
            }}
            disabled={money < productionCost}
            style={{
              fontSize: '18px',
              padding: '20px 50px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px'
            }}
          >
            🎙️ CRÉER UN ÉPISODE
          </button>
          <div style={{ fontSize: '12px', color: '#ffcc00', marginTop: '10px' }}>
            💰 Coût de production: {productionCost}€
          </div>
          {money < productionCost && (
            <div style={{ fontSize: '12px', color: '#ff6b6b', marginTop: '5px' }}>
              ⚠️ Argent insuffisant ({Math.floor(money)}/{productionCost}€)
            </div>
          )}
        </div>
          </div>
          )}

          {/* Panneau pub en pleine largeur */}

          {/* ═══ PRESSION PUBLICITAIRE (Phase 3+) ═══ */}
          {phase >= 3 && fadeStats < 2 && (
            <div className={`panel ${fadeStats === 1 ? 'fade-out' : ''}`} style={{ borderColor: '#ffcc00' }}>
              <h2 style={{ fontSize: '14px', marginBottom: '15px', color: '#ffcc00' }}>
                📢 PRESSION PUBLICITAIRE
              </h2>

              {/* Réglette de pression publicitaire */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '12px',
                  marginBottom: '8px'
                }}>
                  <span>📢 Intensité publicitaire</span>
                  <span style={{
                    color: adPressure < 25 ? '#00ff88' : adPressure < 50 ? '#ffcc00' : adPressure < 75 ? '#ff9900' : '#ff6b6b',
                    fontWeight: '600'
                  }}>
                    {adPressure}%
                  </span>
                </div>

                <input
                  type="range"
                  min="0"
                  max="100"
                  value={adPressure}
                  onChange={(e) => setAdPressure(parseInt(e.target.value))}
                  style={{
                    width: '100%',
                    accentColor: adPressure < 25 ? '#00ff88' : adPressure < 50 ? '#ffcc00' : adPressure < 75 ? '#ff9900' : '#ff6b6b'
                  }}
                />

                {/* Étiquettes */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '10px',
                  color: '#666',
                  marginTop: '5px'
                }}>
                  <span>0% - Minimum</span>
                  <span>50% - Équilibré</span>
                  <span>100% - Maximum</span>
                </div>
              </div>

              {/* Effets de la pression pub */}
              <div style={{
                background: '#252540',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '15px'
              }}>
                <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px' }}>📊 Effets actuels :</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', fontSize: '11px' }}>
                  <div style={{
                    color: adPressure > 0 ? '#00ff88' : '#666'
                  }}>
                    💰 Revenus: <span style={{ fontWeight: 'bold' }}>+{adPressure}%</span>
                  </div>
                  <div style={{
                    color: adPressure > 0 ? '#ff6b6b' : '#00ff88'
                  }}>
                    👥 Conversion: <span style={{ fontWeight: 'bold' }}>{adPressure > 0 ? '-' + Math.floor(adPressure * 0.5) : '0'}%</span>
                  </div>
                  <div style={{
                    color: adPressure > 50 ? '#ff6b6b' : '#666'
                  }}>
                    🤖 Bots: <span style={{ fontWeight: 'bold' }}>{adPressure > 50 ? '-' + Math.floor(adPressure * 0.5) : '0'}%</span>
                  </div>
                </div>
                <div style={{ fontSize: '10px', color: '#666', marginTop: '5px', fontStyle: 'italic' }}>
                  Affecte épisodes, revenus passifs et bots sociaux
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Colonne droite: Journal */}
        <div className="panel" style={{ display: 'flex', flexDirection: 'column', maxHeight: '500px' }}>
          <h2 style={{ marginTop: 0, borderBottom: '1px solid #00ff8844', paddingBottom: '10px' }}>
            📜 JOURNAL
          </h2>
          <div className="log-container" style={{
            flex: 1,
            overflowY: 'auto',
            paddingRight: '5px'
          }}>
            {log.slice(-50).map((entry, i) => (
              <div key={i} className="log-entry">{entry}</div>
            ))}
          </div>
        </div>

        {/* Episode Creator Modal */}
      {showCreator && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: creatorStep === 3 ? '#0a0a15' : 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            
            {/* STEP 1: Format & Subject Selection */}
            {creatorStep === 1 && (
              <div style={{
                background: '#1a1a2e',
                border: '2px solid #00ff88',
                borderRadius: '12px',
                padding: '30px',
                maxWidth: '600px',
                width: '90%',
                animation: 'popIn 0.3s ease-out'
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '25px',
                  paddingBottom: '15px',
                  borderBottom: '1px solid #333'
                }}>
                  <h2 style={{ margin: 0, color: '#00ff88' }}>🎙️ Étape 1 : Concept</h2>
                  <button
                    onClick={() => setShowCreator(false)}
                    style={{ background: 'transparent', border: 'none', color: '#666', fontSize: '24px', cursor: 'pointer' }}
                  >×</button>
                </div>

                {/* Format Selection */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ color: '#00ff88', fontWeight: 600, marginBottom: '12px', fontSize: '14px' }}>
                    📋 FORMAT
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                    {Object.entries(FORMATS).map(([key, format]) => {
                      const isUnlocked = unlockedFormats.includes(key);
                      const isSelected = selectedFormat === key;
                      return (
                        <button
                          key={key}
                          onClick={() => isUnlocked && setSelectedFormat(key)}
                          disabled={!isUnlocked}
                          style={{
                            padding: '15px 10px',
                            background: isSelected ? 'linear-gradient(135deg, #00ff8822, #00ff8811)' : isUnlocked ? '#252540' : '#1a1a25',
                            border: `2px solid ${isSelected ? '#00ff88' : isUnlocked ? '#333' : '#222'}`,
                            color: isSelected ? '#00ff88' : isUnlocked ? '#aaa' : '#444',
                            borderRadius: '10px',
                            cursor: isUnlocked ? 'pointer' : 'not-allowed',
                            fontFamily: 'inherit',
                            fontSize: '12px',
                            transition: 'all 0.2s',
                            textAlign: 'center',
                            opacity: isUnlocked ? 1 : 0.5
                          }}
                        >
                          <div style={{ fontSize: '24px', marginBottom: '5px' }}>
                            {format.name.split(' ')[0]}
                          </div>
                          <div style={{ fontSize: '11px' }}>
                            {isUnlocked ? format.name.split(' ').slice(1).join(' ') : '🔒 ' + formatNumber(format.unlockAt) + ' DL'}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                {/* Subject Selection */}
                <div style={{ marginBottom: '25px' }}>
                  <div style={{ color: '#ffcc00', fontWeight: 600, marginBottom: '12px', fontSize: '14px' }}>
                    🎯 SUJET
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
                    {Object.entries(SUBJECTS).map(([key, subject]) => {
                      const isUnlocked = unlockedSubjects.includes(key);
                      const isSelected = selectedSubject === key;
                      return (
                        <button
                          key={key}
                          onClick={() => isUnlocked && setSelectedSubject(key)}
                          disabled={!isUnlocked}
                          style={{
                            padding: '12px 5px',
                            background: isSelected ? 'linear-gradient(135deg, #ffcc0022, #ffcc0011)' : isUnlocked ? '#252540' : '#1a1a25',
                            border: `2px solid ${isSelected ? '#ffcc00' : isUnlocked ? '#333' : '#222'}`,
                            color: isSelected ? '#ffcc00' : isUnlocked ? '#aaa' : '#444',
                            borderRadius: '8px',
                            cursor: isUnlocked ? 'pointer' : 'not-allowed',
                            fontFamily: 'inherit',
                            fontSize: '10px',
                            transition: 'all 0.2s',
                            textAlign: 'center',
                            opacity: isUnlocked ? 1 : 0.5
                          }}
                        >
                          <div style={{ fontSize: '20px', marginBottom: '3px' }}>
                            {subject.name.split(' ')[0]}
                          </div>
                          <div style={{ fontSize: '9px' }}>
                            {isUnlocked ? subject.name.split(' ').slice(1).join(' ') : '🔒'}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Selection recap */}
                <div style={{ 
                  padding: '15px',
                  background: '#252540',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  textAlign: 'center'
                }}>
                  <span style={{ color: '#00ff88', fontSize: '16px' }}>{FORMATS[selectedFormat]?.name}</span>
                  <span style={{ color: '#444', margin: '0 15px', fontSize: '20px' }}>×</span>
                  <span style={{ color: '#ffcc00', fontSize: '16px' }}>{SUBJECTS[selectedSubject]?.name}</span>
                </div>

                <button
                  className="btn"
                  onClick={() => setCreatorStep(2)}
                  style={{ 
                    width: '100%', 
                    padding: '15px',
                    fontSize: '16px',
                    borderColor: '#00ff88',
                    color: '#00ff88',
                    background: 'linear-gradient(135deg, #00ff8822, transparent)'
                  }}
                >
                  Continuer → Enregistrement
                </button>
              </div>
            )}

            {/* STEP 2: Sliders */}
            {creatorStep === 2 && (
              <div style={{
                background: '#1a1a2e',
                border: '2px solid #00ccff',
                borderRadius: '12px',
                padding: '30px',
                maxWidth: '500px',
                width: '90%',
                animation: 'popIn 0.3s ease-out'
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '20px',
                  paddingBottom: '15px',
                  borderBottom: '1px solid #333'
                }}>
                  <h2 style={{ margin: 0, color: '#00ccff' }}>🎙️ Étape 2 : Enregistrement</h2>
                </div>

                {/* Current selection recap */}
                <div style={{ 
                  textAlign: 'center', 
                  marginBottom: '25px',
                  padding: '12px',
                  background: '#252540',
                  borderRadius: '8px'
                }}>
                  <span style={{ color: '#00ff88' }}>{FORMATS[selectedFormat]?.name}</span>
                  <span style={{ color: '#444', margin: '0 10px' }}>×</span>
                  <span style={{ color: '#ffcc00' }}>{SUBJECTS[selectedSubject]?.name}</span>
                </div>

                <div style={{ fontSize: '12px', color: '#666', textAlign: 'center', marginBottom: '20px' }}>
                  Répartis ton temps entre les 3 phases
                </div>

                {/* Sliders - NO OPTIMAL VALUES SHOWN */}
                <div style={{ marginBottom: '20px' }}>
                  {/* Préparation */}
                  <div style={{ 
                    background: '#252540', 
                    borderRadius: '8px', 
                    padding: '12px 15px',
                    marginBottom: '10px',
                    border: '1px solid #00ccff44'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ color: '#00ccff' }}>📝 Préparation</span>
                      <span style={{ color: '#00ccff', fontWeight: 'bold' }}>{sliderPrep}%</span>
                    </div>
                    <input
                      type="range" min="0" max="100" value={sliderPrep}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        const remaining = 100 - val;
                        const ratio = sliderPerf + sliderProd > 0 ? sliderPerf / (sliderPerf + sliderProd) : 0.5;
                        setSliderPrep(val);
                        setSliderPerf(Math.round(remaining * ratio));
                        setSliderProd(remaining - Math.round(remaining * ratio));
                      }}
                      style={{ width: '100%', accentColor: '#00ccff' }}
                    />
                  </div>
                  
                  {/* Performance */}
                  <div style={{ 
                    background: '#252540', 
                    borderRadius: '8px', 
                    padding: '12px 15px',
                    marginBottom: '10px',
                    border: '1px solid #ff6b6b44'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ color: '#ff6b6b' }}>🎭 Performance</span>
                      <span style={{ color: '#ff6b6b', fontWeight: 'bold' }}>{sliderPerf}%</span>
                    </div>
                    <input
                      type="range" min="0" max="100" value={sliderPerf}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        const remaining = 100 - val;
                        const ratio = sliderPrep + sliderProd > 0 ? sliderPrep / (sliderPrep + sliderProd) : 0.5;
                        setSliderPerf(val);
                        setSliderPrep(Math.round(remaining * ratio));
                        setSliderProd(remaining - Math.round(remaining * ratio));
                      }}
                      style={{ width: '100%', accentColor: '#ff6b6b' }}
                    />
                  </div>
                  
                  {/* Production */}
                  <div style={{ 
                    background: '#252540', 
                    borderRadius: '8px', 
                    padding: '12px 15px',
                    border: '1px solid #ffcc0044'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ color: '#ffcc00' }}>🎚️ Production</span>
                      <span style={{ color: '#ffcc00', fontWeight: 'bold' }}>{sliderProd}%</span>
                    </div>
                    <input
                      type="range" min="0" max="100" value={sliderProd}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        const remaining = 100 - val;
                        const ratio = sliderPrep + sliderPerf > 0 ? sliderPrep / (sliderPrep + sliderPerf) : 0.5;
                        setSliderProd(val);
                        setSliderPrep(Math.round(remaining * ratio));
                        setSliderPerf(remaining - Math.round(remaining * ratio));
                      }}
                      style={{ width: '100%', accentColor: '#ffcc00' }}
                    />
                  </div>
                </div>

                {/* Balance bar */}
                <div style={{ 
                  height: '12px', 
                  background: '#111', 
                  borderRadius: '6px', 
                  display: 'flex', 
                  overflow: 'hidden',
                  marginBottom: '25px',
                  border: '1px solid #333'
                }}>
                  <div style={{ width: `${sliderPrep}%`, background: '#00ccff', transition: 'width 0.2s' }} />
                  <div style={{ width: `${sliderPerf}%`, background: '#ff6b6b', transition: 'width 0.2s' }} />
                  <div style={{ width: `${sliderProd}%`, background: '#ffcc00', transition: 'width 0.2s' }} />
                </div>

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    className="btn"
                    onClick={() => setCreatorStep(1)}
                    style={{ flex: 1, padding: '12px', borderColor: '#666', color: '#888' }}
                  >
                    ← Retour
                  </button>
                  <button 
                    className="btn"
                    onClick={() => setCreatorStep(3)}
                    style={{ flex: 2, padding: '12px', fontSize: '16px', borderColor: '#00ff88', color: '#00ff88' }}
                  >
                    Continuer →
                  </button>
                </div>
              </div>
            )}

            {/* STEP 3: Equipment (display current + locked) */}
            {creatorStep === 3 && (
              <div style={{
                background: '#1a1a2e',
                border: '2px solid #ff9900',
                borderRadius: '12px',
                padding: '30px',
                maxWidth: '500px',
                width: '90%',
                animation: 'popIn 0.3s ease-out'
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '20px',
                  paddingBottom: '15px',
                  borderBottom: '1px solid #333'
                }}>
                  <h2 style={{ margin: 0, color: '#ff9900' }}>🎛️ Étape 3 : Équipement</h2>
                </div>

                <div style={{ fontSize: '12px', color: '#666', textAlign: 'center', marginBottom: '20px' }}>
                  Ton setup actuel • Débloque les upgrades dans le panneau latéral
                </div>

                {/* Micro Display */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ color: '#ff9900', fontWeight: 600, marginBottom: '10px', fontSize: '14px' }}>
                    🎤 MICRO
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {MIC_UPGRADES.map((mic, index) => {
                      const isUnlocked = index <= micLevel;
                      const isCurrent = index === micLevel;
                      return (
                        <div
                          key={index}
                          style={{
                            padding: '12px 15px',
                            background: isCurrent ? '#ff990022' : '#252540',
                            border: `2px solid ${isCurrent ? '#ff9900' : isUnlocked ? '#444' : '#333'}`,
                            borderRadius: '8px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            opacity: isUnlocked ? 1 : 0.5
                          }}
                        >
                          <div style={{ textAlign: 'left' }}>
                            <div style={{ color: isCurrent ? '#ff9900' : isUnlocked ? '#888' : '#555', fontWeight: 'bold' }}>
                              {isCurrent ? '✓ ' : ''}{mic.name}
                              {!isUnlocked && ' 🔒'}
                            </div>
                            <div style={{ fontSize: '11px', color: '#666' }}>{mic.desc}</div>
                          </div>
                          <div style={{ 
                            color: isCurrent ? '#00ff88' : '#666',
                            fontWeight: 'bold',
                            fontSize: '12px'
                          }}>
                            {isCurrent ? 'Équipé' : isUnlocked ? 'Débloqué' : `${mic.cost}€`}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Editing Display */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ color: '#ff9900', fontWeight: 600, marginBottom: '10px', fontSize: '14px' }}>
                    ✂️ MONTAGE
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {EDITING_UPGRADES.map((edit, index) => {
                      const isUnlocked = index <= editingLevel;
                      const isCurrent = index === editingLevel;
                      return (
                        <div
                          key={index}
                          style={{
                            padding: '12px 15px',
                            background: isCurrent ? '#ff990022' : '#252540',
                            border: `2px solid ${isCurrent ? '#ff9900' : isUnlocked ? '#444' : '#333'}`,
                            borderRadius: '8px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            opacity: isUnlocked ? 1 : 0.5
                          }}
                        >
                          <div style={{ textAlign: 'left' }}>
                            <div style={{ color: isCurrent ? '#ff9900' : isUnlocked ? '#888' : '#555', fontWeight: 'bold' }}>
                              {isCurrent ? '✓ ' : ''}{edit.name}
                              {!isUnlocked && ' 🔒'}
                            </div>
                            <div style={{ fontSize: '11px', color: '#666' }}>{edit.desc}</div>
                          </div>
                          <div style={{ 
                            color: isCurrent ? '#00ff88' : '#666',
                            fontWeight: 'bold',
                            fontSize: '12px'
                          }}>
                            {isCurrent ? 'Équipé' : isUnlocked ? 'Débloqué' : `${edit.cost}€`}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Equipment bonus summary */}
                <div style={{ 
                  padding: '12px',
                  background: '#252540',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  display: 'flex',
                  justifyContent: 'space-between'
                }}>
                  <span style={{ color: '#888' }}>Bonus équipement</span>
                  <span style={{ color: '#00ff88', fontWeight: 'bold' }}>
                    ×{(micBonus * editingBonus).toFixed(2)} downloads
                  </span>
                </div>

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    className="btn"
                    onClick={() => setCreatorStep(2)}
                    style={{ flex: 1, padding: '12px', borderColor: '#666', color: '#888' }}
                  >
                    ← Retour
                  </button>
                  <button 
                    className="btn"
                    onClick={() => setCreatorStep(4)}
                    style={{ flex: 2, padding: '12px', fontSize: '16px', borderColor: '#00ff88', color: '#00ff88' }}
                  >
                    Continuer →
                  </button>
                </div>
              </div>
            )}

            {/* STEP 4: Promotion/Boosts */}
            {creatorStep === 4 && (
              <div style={{
                background: '#1a1a2e',
                border: '2px solid #cc66ff',
                borderRadius: '12px',
                padding: '30px',
                maxWidth: '500px',
                width: '90%',
                animation: 'popIn 0.3s ease-out'
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '20px',
                  paddingBottom: '15px',
                  borderBottom: '1px solid #333'
                }}>
                  <h2 style={{ margin: 0, color: '#cc66ff' }}>📣 Étape 4 : Promotion</h2>
                </div>

                <div style={{ fontSize: '12px', color: '#666', textAlign: 'center', marginBottom: '20px' }}>
                  Options payantes pour booster ton épisode
                </div>

                {/* Boost options - filtered by milestone unlocks */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
                  {Object.entries(BOOSTS)
                    .filter(([key]) => isUnlocked(`boost_${key}`))
                    .map(([key, boost]) => {
                      const currentBoostCost = getBoostCost(key);
                      return (
                    <button
                      key={key}
                      onClick={() => setEpisodeBoosts(prev => ({ ...prev, [key]: !prev[key] }))}
                      disabled={!episodeBoosts[key] && money < currentBoostCost + boostCost}
                      style={{
                        padding: '12px 15px',
                        background: episodeBoosts[key] ? '#cc66ff22' : '#252540',
                        border: `2px solid ${episodeBoosts[key] ? '#cc66ff' : '#333'}`,
                        borderRadius: '8px',
                        cursor: (!episodeBoosts[key] && money < currentBoostCost + boostCost) ? 'not-allowed' : 'pointer',
                        fontFamily: 'inherit',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        transition: 'all 0.2s',
                        opacity: (!episodeBoosts[key] && money < currentBoostCost + boostCost) ? 0.5 : 1
                      }}
                    >
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ color: episodeBoosts[key] ? '#cc66ff' : '#aaa', fontWeight: 'bold' }}>
                          {episodeBoosts[key] ? '✓ ' : ''}{boost.name}
                        </div>
                        <div style={{ fontSize: '11px', color: '#666' }}>
                          {boost.dlBonus > 0 && `+${boost.dlBonus * 100}% downloads`}
                          {boost.dlBonus > 0 && boost.subBonus > 0 && ' · '}
                          {boost.subBonus > 0 && `+${boost.subBonus * 100}% abonnés`}
                        </div>
                      </div>
                      <div style={{ color: '#ff6b6b', fontWeight: 'bold' }}>
                        -{currentBoostCost}€
                      </div>
                    </button>
                      );
                    })}
                  {Object.entries(BOOSTS).filter(([key]) => isUnlocked(`boost_${key}`)).length === 0 && (
                    <div style={{ color: '#666', textAlign: 'center', padding: '20px' }}>
                      🔒 Boosts débloqués progressivement...
                    </div>
                  )}
                </div>

                {/* Total cost summary */}
                <div style={{ 
                  padding: '15px',
                  background: '#252540',
                  borderRadius: '8px',
                  marginBottom: '20px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ color: '#888' }}>Production</span>
                    <span style={{ color: '#ffcc00' }}>
                      {effectiveProductionCost}€
                      {lobbyingLevel > 0 && (
                        <span style={{ color: '#00ff88', fontSize: '10px', marginLeft: '5px' }}>
                          (-{Math.round((1 - lobbyingDiscount) * 100)}%)
                        </span>
                      )}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ color: '#888' }}>Promotion</span>
                    <span style={{ color: '#cc66ff' }}>{boostCost}€</span>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    paddingTop: '8px',
                    borderTop: '1px solid #444'
                  }}>
                    <span style={{ color: '#fff', fontWeight: 'bold' }}>TOTAL</span>
                    <span style={{ 
                      color: totalEpisodeCost > money ? '#ff6b6b' : '#ffcc00', 
                      fontWeight: 'bold',
                      fontSize: '18px'
                    }}>
                      {totalEpisodeCost}€
                    </span>
                  </div>
                  <div style={{ fontSize: '11px', color: '#666', textAlign: 'right', marginTop: '5px' }}>
                    (Tu as {Math.floor(money)}€)
                  </div>
                </div>

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    className="btn"
                    onClick={() => setCreatorStep(3)}
                    style={{ flex: 1, padding: '12px', borderColor: '#666', color: '#888' }}
                  >
                    ← Retour
                  </button>
                  <button 
                    className="btn btn-record"
                    onClick={() => setCreatorStep(5)}
                    disabled={totalEpisodeCost > money}
                    style={{ flex: 2, padding: '12px', fontSize: '16px' }}
                  >
                    ● REC - ENREGISTRER
                  </button>
                </div>
                {totalEpisodeCost > money && (
                  <div style={{ fontSize: '11px', color: '#ff6b6b', textAlign: 'center', marginTop: '10px' }}>
                    ⚠️ Pas assez d'argent
                  </div>
                )}
              </div>
            )}

            {/* STEP 5: Recording Animation */}
            {creatorStep === 5 && (
              <RecordingAnimation 
                onComplete={() => {
                  // Check if subject matches an active trend (après 2ème épisode)
                  let trendMultiplierDL = 1.0;
                  let trendMultiplierSubs = 1.0;
                  let matchedTrend = null;
                  
                  if (episodes >= TREND_UNLOCK_EPISODES && activeTrends.length > 0) {
                    matchedTrend = activeTrends.find(trend => 
                      trend.subjects.includes(selectedSubject)
                    );
                    
                    if (matchedTrend) {
                      trendMultiplierDL = TREND_BOOST_DL;
                      trendMultiplierSubs = TREND_BOOST_SUBS;
                    }
                  }
                  
                  // PRESSION PUBLICITAIRE (Phase 3+)
                  const adRevenueMultiplier = 1 + (adPressure / 100);
                  const adSubPenalty = 1 - (adPressure * 0.005);
                  
                  // Freeze results at this moment with trend bonus
                  const finalDownloads = Math.floor(qualityDownloadsPerEpisode * trendMultiplierDL);
                  const finalSubs = Math.floor(finalDownloads * qualitySubConversion * trendMultiplierSubs * adSubPenalty);
                  const baseRevenue = finalDownloads * revenuePerDownload;
                  const finalRevenue = baseRevenue * adRevenueMultiplier;
                  const adBonus = finalRevenue - baseRevenue;
                  
                  // Get slider feedback
                  const sliderFeedback = getSliderFeedback(selectedFormat, sliderPrep, sliderPerf, sliderProd);
                  
                  // Store snapshot for display (3 categories only, no promo)
                  setEpisodeResults({
                    downloads: finalDownloads,
                    subscribers: finalSubs,
                    revenue: finalRevenue,
                    conceptScore: conceptScoreFinal,
                    balanceScore,
                    equipScore,
                    finalScore,
                    sliderFeedback,
                    trendBonus: matchedTrend ? true : false,
                    adBonus: adBonus > 0 ? adBonus : null,
                    adPressure
                  });
                  
                  // Apply results
                  setEpisodes(e => e + 1);
                  setCatalogHype(Math.floor(500 * Math.log10(episodes + 2))); // Full hype reset! New episode = max hype (log scale)
                  setDownloads(d => d + finalDownloads);
                  setSubscribers(s => s + finalSubs);
                  setMoney(m => m + finalRevenue);
                  // Note: lastCombo is set when clicking "Continuer" to avoid showing repeat penalty on current episode
                  
                  // Plus de système de sponsors/contrats
                  
                  // Log the trend bonus
                  if (matchedTrend) {
                    addLog(`🔥 TENDANCE ! Ton sujet est viral ! (+50% DL & subs)`);
                  }

                  // Log ad bonus
                  if (adBonus > 0) {
                    addLog(`📢 Publicités (${adPressure}%): +${adBonus.toFixed(0)}€ revenus, -${Math.floor(adPressure * 0.5)}% abonnés`);
                  }
                  
                  setCreatorStep(6);
                }}
                sliderPrep={sliderPrep}
                sliderPerf={sliderPerf}
                sliderProd={sliderProd}
              />
            )}

            {/* STEP 6: Results */}
            {creatorStep === 6 && episodeResults && (
              <ResultsAnimation
                categories={[
                  { 
                    name: '🎯 Concept', 
                    subtitle: `${FORMATS[selectedFormat]?.name} × ${SUBJECTS[selectedSubject]?.name}`,
                    score: episodeResults.conceptScore,
                    penalty: repeatPenalty < 1 ? '(répétition -30%)' : null
                  },
                  { 
                    name: '🎙️ Enregistrement', 
                    subtitle: `Prépa ${sliderPrep}% · Perf ${sliderPerf}% · Prod ${sliderProd}%`,
                    score: episodeResults.balanceScore 
                  },
                  { 
                    name: '🎛️ Équipement', 
                    subtitle: `${MIC_UPGRADES[micLevel]?.name} · ${EDITING_UPGRADES[editingLevel]?.name}`,
                    score: episodeResults.equipScore 
                  }
                ]}
                finalScore={episodeResults.finalScore}
                episodeResults={episodeResults}
                formatNumber={formatNumber}
                boostsUsed={Object.entries(episodeBoosts).filter(([_,v]) => v).map(([k]) => BOOSTS[k]?.name)}
                trendBonus={episodeResults.trendBonus}
                onContinue={() => {
                  setShowCreator(false);
                  setEpisodeResults(null);
                  setLastCombo(`${selectedFormat}-${selectedSubject}`);
                  const trendNote = episodeResults.trendBonus ? ' 🔥TREND!' : '';
                  addLog(`🎙️ Ep.${episodes} ${FORMATS[selectedFormat]?.name} × ${SUBJECTS[selectedSubject]?.name} (${episodeResults.finalScore.toFixed(1)}/10)${trendNote} → +${formatNumber(episodeResults.downloads)} DL, +${episodeResults.subscribers} subs, ${episodeResults.revenue >= 0 ? '+' : ''}${episodeResults.revenue.toFixed(0)}€`);
                }}
              />
            )}
          </div>
        )}

        {/* Upgrades by Category */}
        {fadeUpgrades < 2 && (
        <div className={`${glitchTarget === 'upgrades' ? 'glitch-intense' : ''} ${fadeUpgrades === 1 ? 'fade-out' : ''}`}>
        {Object.entries(upgradeCategories).map(([catKey, category]) => {
          // Marketing only visible in phase 2+
          if (catKey === 'marketing' && phase < 2) return null;

          const categoryUpgrades = Object.entries(upgrades).filter(
            ([key, u]) => u.category === catKey && u.available
          );
          if (categoryUpgrades.length === 0) return null;

          return (
            <div key={catKey} className="panel" style={{ marginTop: '20px' }}>
              <h2 style={{
                marginTop: 0,
                borderBottom: `1px solid ${category.color}44`,
                paddingBottom: '10px',
                color: category.color
              }}>
                {category.name}
              </h2>
              <div className="upgrade-grid">
                {categoryUpgrades.map(([key, upgrade]) => (
                  <div key={key} className="upgrade-card" style={{ borderColor: `${category.color}33` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <div>
                        <div style={{ fontWeight: 600, color: category.color }}>{upgrade.name}</div>
                        <div style={{ fontSize: '11px', color: '#00ff8888' }}>{upgrade.description}</div>
                      </div>
                      <button
                        className="btn"
                        onClick={upgrade.effect}
                        disabled={money < upgrade.cost}
                        style={{
                          whiteSpace: 'nowrap',
                          borderColor: category.color,
                          color: category.color
                        }}
                      >
                        {formatNumber(upgrade.cost)} €
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        </div>
        )}

        {/* Current stats display */}
        {(autoProducers > 0 || aiHosts > 0 || podcastNetworks > 0) && fadeUpgrades < 2 && (
          <div className={`panel ${fadeUpgrades === 1 ? 'fade-out' : ''}`} style={{ marginTop: '20px' }}>
            <h2 style={{ marginTop: 0, borderBottom: '1px solid #00ff8844', paddingBottom: '10px' }}>
              🏭 INFRASTRUCTURE
            </h2>
            <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
              {autoProducers > 0 && <div>👨‍💻 Assistants: {autoProducers}</div>}
              {aiHosts > 0 && <div>🤖 Hôtes IA: {aiHosts}</div>}
              {socialBots > 0 && <div>🤖 Bots sociaux: {socialBots}</div>}
              {podcastNetworks > 0 && <div>🌐 Réseaux: {podcastNetworks}</div>}
              {universeConversion > 0 && <div style={{ color: '#cc00ff' }}>🌌 Convertisseurs: {universeConversion}</div>}
            </div>

            {/* Auto production details */}
            {autoProductionRate > 0 && (
              <div style={{
                marginTop: '15px',
                paddingTop: '15px',
                borderTop: '1px solid #00ff8822',
                fontSize: '12px',
                color: '#aaa'
              }}>
                <div style={{ marginBottom: '8px', color: '#00ff88' }}>📊 Production automatique</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <div>⏱️ Vitesse: {autoProductionRate.toFixed(2)} ep/sec</div>
                  <div>⏳ Prochain: {autoProductionRate > 0 ? `${Math.ceil((1 - (autoEpisodeAccumRef.current % 1)) / autoProductionRate)}s` : '-'}</div>
                  <div>💰 Coût/ep: {Math.floor(productionCost * 0.5)}€</div>
                  <div>🔥 Hype/ep: +{Math.floor(maxHype * 0.2)}</div>
                </div>
                {money < Math.floor(productionCost * 0.5) && (
                  <div style={{ color: '#ff6b6b', marginTop: '8px' }}>
                    ⚠️ Production en pause (argent insuffisant)
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      )}

        {/* Debug Panel (Ctrl+Shift+D to toggle) */}
        {showDebugPanel && (
          <div style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            background: 'rgba(255, 100, 100, 0.95)',
            border: '2px solid #ff4444',
            borderRadius: '10px',
            padding: '15px',
            zIndex: 9999,
            minWidth: '250px',
            boxShadow: '0 0 20px rgba(255, 0, 0, 0.5)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <span style={{ fontWeight: 'bold', color: '#fff' }}>🔧 DEBUG PANEL</span>
              <button
                onClick={() => setShowDebugPanel(false)}
                style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '16px' }}
              >✕</button>
            </div>

            <div style={{ marginBottom: '10px' }}>
              <label style={{ color: '#fff', fontSize: '12px', display: 'block', marginBottom: '5px' }}>
                Score épisode: {debugScore.toFixed(1)}/10
              </label>
              <input
                type="range"
                min="1"
                max="10"
                step="0.5"
                value={debugScore}
                onChange={(e) => setDebugScore(parseFloat(e.target.value))}
                style={{ width: '100%' }}
              />
            </div>

            {(() => {
              const debugTotalBoostCost = Object.entries(debugBoosts).filter(([_,v]) => v).reduce((sum, [k]) => sum + getBoostCost(k), 0);
              const canAfford = money >= productionCost + debugTotalBoostCost;
              return (
                <button
                  onClick={debugGenerateEpisode}
                  disabled={!canAfford}
                  style={{
                    width: '100%',
                    padding: '10px',
                    background: canAfford ? '#ff4444' : '#666',
                    border: 'none',
                    borderRadius: '5px',
                    color: '#fff',
                    fontWeight: 'bold',
                    cursor: canAfford ? 'pointer' : 'not-allowed',
                    fontFamily: 'inherit',
                    opacity: canAfford ? 1 : 0.5
                  }}
                >
                  ⚡ Générer épisode ({debugScore}/10)
                </button>
              );
            })()}

            <div style={{ marginTop: '15px', paddingTop: '10px', borderTop: '1px solid #ff666666' }}>
              <label style={{ color: '#fff', fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                🚀 Boosts
              </label>
              {Object.entries(BOOSTS).map(([key, boost]) => (
                <label key={key} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '5px',
                  fontSize: '11px',
                  color: debugBoosts[key] ? '#fff' : '#ffcccc',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={debugBoosts[key]}
                    onChange={(e) => setDebugBoosts(b => ({ ...b, [key]: e.target.checked }))}
                  />
                  {boost.name} ({getBoostCost(key)}€)
                  <span style={{ color: '#ff8888', fontSize: '10px' }}>
                    {boost.dlBonus > 0 ? `+${boost.dlBonus * 100}%DL` : ''}
                    {boost.subBonus > 0 ? ` +${boost.subBonus * 100}%Sub` : ''}
                  </span>
                </label>
              ))}
              <div style={{ fontSize: '10px', color: '#ffcccc', marginTop: '5px' }}>
                Coût total: {productionCost + Object.entries(debugBoosts).filter(([_,v]) => v).reduce((sum, [k]) => sum + getBoostCost(k), 0)}€
              </div>
            </div>

            <div style={{ marginTop: '10px', fontSize: '10px', color: '#ffcccc', textAlign: 'center' }}>
              Ctrl+Shift+D pour masquer
            </div>
          </div>
        )}

        {/* Reset link */}
        <div style={{
          marginTop: '50px',
          textAlign: 'center',
          paddingTop: '20px',
          borderTop: '1px solid #00ff8811'
        }}>
          <button
            onClick={() => {
              if (window.confirm('Êtes-vous sûr de vouloir recommencer à zéro ? Toute progression sera perdue.')) {
                // localStorage.removeItem('universalPodcastSave'); // Disabled
                window.location.reload();
              }
            }}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#00ff8844',
              fontSize: '11px',
              cursor: 'pointer',
              fontFamily: 'inherit',
              textDecoration: 'underline',
              padding: '5px 10px'
            }}
            onMouseEnter={(e) => e.target.style.color = '#00ff88'}
            onMouseLeave={(e) => e.target.style.color = '#00ff8844'}
          >
            🔄 Recommencer à zéro
          </button>
        </div>

    </div>
  );
};

export default UniversalPodcast;